import { policyFor, repairLine, worded } from "./rule-catalog";
import {
  STUDY_OBJECTIVE_MAX_BULLETS,
  STUDY_OBJECTIVE_MAX_CHARS,
} from "./index";
import type { QuestionnaireDraft, QuestionnaireValidationIssue } from "./index";
import { assetComparisonCoherenceIssues } from "./asset-comparison-coherence";
import { exposureCoherenceIssues } from "./exposure-coherence";
import { recalledReadIssues, testedAssetUnusedIssues } from "./tested-asset-coverage";
import { answerabilityIssues } from "./answerability";
import {
  isSurveyDataOnlyQuestion,
  reportingCoherenceIssues,
} from "./reporting-coherence";
import {
  MAX_ASSET_DESCRIPTION_WORDS,
  isWithinAssetDescriptionWordLimit,
} from "./asset-description-limits";
import { MAX_QUESTION_LABEL_LENGTH } from "./text-limits";
import { readLiftTag } from "./lift-tags";
import { recipeDeclarations, studySpecOf, type StudySpec } from "./study-spec";
import { assetMentionIssuesIn, renderAssetRefTokens } from "./asset-ref-tokens";
import {
  DEFAULT_RESEARCH_QUESTION_FLOOR,
  MATRIX_MAX_COLUMNS,
  MIN_CHOICE_MEMBERS,
  QUESTION_CAPABILITIES,
  RESEARCH_QUESTION_CEILING,
} from "./question-capabilities";
import { openEndClassifierMetricId } from "./open-end-classifier";
import { isQualitativeSlideType } from "./canonical-vocabularies";
import {
  YOUTUBE_NON_KEYFRAMEABLE_MESSAGE,
  nonKeyframeableVideoMessage,
} from "./video-url";
import {
  ASSET_FAMILY_FACTS,
  DESCRIPTION_REQUIRED_FAMILIES,
  assetFamilyTitleNoun,
  isAssetFamily,
} from "./asset-families";
import { respondentTextGluesQuestionStems } from "./question-stem-guard";
import {
  recipeModuleTypeMismatchOf,
} from "./recipe-module-question-type";
import {
  DURATION_EXPOSED_UNITS,
  getUnitRegistryEntry,
  isContinuousUnitId,
  QUESTION_TYPE_IDS,
  isRatingUnitId,
  ratingBounds,
  ratingHasMidpoint,
  unitRequiresExplicitBoundsForComposite,
} from "./registry";
import {
  POINT_LABEL_RATING_UNIT_IDS,
  RATING_SCALES,
  SUPPORTED_RATING_UNIT_IDS,
  englishList,
} from "./rating-scales";
import { exactChoiceAssetRefAnswerLabel } from "./asset-items";
import type { PriceWording } from "./product-version-label";
import { respondentWordingFor } from "./scale-wording-packs";
import {
  CONDITION_OPERATOR_FAMILIES,
  MAX_VISIBILITY_RULE_GROUP_SIZE,
  NUMERIC_CONDITION_OPERATORS,
  conditionMembershipOperator,
  conditionOperatorFamily,
} from "./condition-operators";
import type { ConditionOperatorFamily } from "./condition-operators";
import {
  countWithinBounds,
  fieldAllowedForType,
  operatorReadsSource,
  scaleExecutable,
  valueInList,
} from "./rule-evaluators";
import type { RuleFailure } from "./rule-evaluators";

/**
 * How many items a ranking asks for (decision O2). With no stated bound it is
 * full: the respondent ranks every item offered, compiled as a plain SurveyJS
 * ranking. So is a ranking of its own fixed items whose bounds are exactly
 * all of them. A maximum alone asks for exactly that many; a stated minimum
 * is kept as authored, up to the stated maximum or every item. A list carried
 * forward keeps its stated bounds: the items it offers vary by respondent, so
 * its minimum stays a count the source must be able to fill.
 */
export type RankingBounds =
  | { kind: "full" }
  | { kind: "partial"; min: number; max: number };

export function rankingBounds(response: JsonObject): RankingBounds {
  const stated = (value: unknown): number | undefined =>
    typeof value === "number" && Number.isInteger(value) && value > 0
      ? value
      : undefined;
  const min = stated(response.minRankedItems);
  const max = stated(response.maxRankedItems);
  if (min === undefined && max === undefined) {
    return { kind: "full" };
  }
  const itemCount = asArray(response.options).length;
  const upper = max ?? itemCount;
  const lower = min ?? upper;
  const everyOwnItem =
    !isRecord(response.choicesFrom) &&
    itemCount > 0 &&
    lower === itemCount &&
    upper === itemCount;
  return everyOwnItem
    ? { kind: "full" }
    : { kind: "partial", min: lower, max: upper };
}

/** The most items a respondent can rank: every item, or the stated maximum. */
export function rankingMaxLength(response: JsonObject): number {
  const bounds = rankingBounds(response);
  return bounds.kind === "full" ? asArray(response.options).length : bounds.max;
}

/**
 * How many items every respondent ranks, when that is one number: a full
 * ranking of a fixed list, or a partial one whose minimum is its maximum.
 * Null when it varies (a range, or a list carried forward from what each
 * respondent selected), where the bottom positions hold different items.
 */
export function rankingFixedLength(response: JsonObject): number | null {
  const choicesFrom = response.choicesFrom;
  if (
    isRecord(choicesFrom) &&
    (choicesFrom.mode === "selected" || choicesFrom.mode === "unselected")
  ) {
    return null;
  }
  const bounds = rankingBounds(response);
  if (bounds.kind === "full") return asArray(response.options).length;
  return bounds.min === bounds.max ? bounds.max : null;
}

type JsonObject = Record<string, unknown>;

/** Inclusive continuous bounds from authored min/max, else unit registry, else none. */
export function continuousThresholdBounds(
  response: JsonObject,
): [number, number] | null {
  const authoredMin =
    typeof response.min === "number" && Number.isFinite(response.min)
      ? response.min
      : undefined;
  const authoredMax =
    typeof response.max === "number" && Number.isFinite(response.max)
      ? response.max
      : undefined;
  if (authoredMin !== undefined || authoredMax !== undefined) {
    return [
      authoredMin ?? Number.NEGATIVE_INFINITY,
      authoredMax ?? Number.POSITIVE_INFINITY,
    ];
  }
  const unit = response.unit;
  if (typeof unit !== "string" || !isContinuousUnitId(unit)) {
    return null;
  }
  const entry = getUnitRegistryEntry(unit);
  if (
    !entry ||
    typeof entry.rawMin !== "number" ||
    typeof entry.rawMax !== "number"
  ) {
    return null;
  }
  return [entry.rawMin, entry.rawMax];
}

export interface DraftValidationResult {
  status: "valid" | "invalid";
  issues: Array<QuestionnaireValidationIssue>;
}

export function isRecord(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function asArray(value: unknown): Array<unknown> {
  return Array.isArray(value) ? value : [];
}

// Matrix column caps match the heatmap slide contract, and are owned by
// `config/questionnaire-question-capabilities.json` (Python reads the same
// file). Column overflow is an error so launch cannot produce a grid the deck
// cannot render. Rows have no questionnaire cap: named-item rosters are study
// inputs, and the deck paginates them.
export const MAX_MATRIX_MEASURES = MATRIX_MAX_COLUMNS;
export const MAX_MATRIX_CATEGORIES = MATRIX_MAX_COLUMNS;

/**
 * Longest authored label the deck can render: matrix measures and rows, matrix
 * categories, choice options.
 *
 * Same reasoning as the column caps above, applied to length instead of count.
 * Nothing used to bound this, so a researcher could write a label the deck's
 * renderer schema rejects, and the study fielded and completed while the report
 * died at the very end -- twice in one QA round. Bounding it here means the
 * failure surfaces in the builder, where it can be edited, instead of hours
 * later in a deck nobody can regenerate.
 *
 * Deliberately generous: far more than any readable column label, and the
 * client clamps what it cannot show. The number is not a design opinion, it is
 * the deck's own `maxLength` for these fields
 * (packages/slide-schemas/presets/*.schema.json), owned by
 * `config/questionnaire-text-limits.json` (`questionLabelMaxChars`), and a
 * parity test asserts the two stay equal -- a builder limit looser than the
 * renderer would let the old failure straight back through.
 */
export { MAX_QUESTION_LABEL_LENGTH };

/**
 * A label the deck cannot render is a launch-time problem, not a report-time
 * one. The renderer rejects an over-length label and the whole report dies,
 * hours after the study finished, with nothing to regenerate -- the text is the
 * researcher's own. Catching it here means they can edit it while they are
 * still looking at it.
 *
 * A warning, not an error: compile and the Review gate block on
 * `severity === 'error'` (not a Build-fatal invariant), and this cap did not exist
 * when older drafts were saved, so blocking would stop a study that compiled
 * yesterday on text the marketer has already fielded. The deck now contains an
 * over-long label rather than dying on it, so this only needs to tell them,
 * early, where it is cheap to fix.
 */
function validateLabelLength(
  issues: Array<QuestionnaireValidationIssue>,
  value: unknown,
  path: string,
  what: string,
): void {
  if (typeof value !== "string") return;
  const [tooLong] = countWithinBounds(
    { max: MAX_QUESTION_LABEL_LENGTH },
    { count: value.length },
  );
  if (tooLong?.reason !== "above_max") return;
  addIssue(
    issues,
    path,
    "label_too_long",
    repairLine("label_too_long", { what: String(what), valueCount: String(value.length), maxValue: String(tooLong.max) }),
    "warning",
  );
}

// The research-question bounds are owned by
// `config/questionnaire-question-capabilities.json` (Python reads the same
// file); they are exported from here too, where the count rule reads them.
export { DEFAULT_RESEARCH_QUESTION_FLOOR, RESEARCH_QUESTION_CEILING };

/**
 * Floor for an active ad instrument. Kept as a named export for source
 * compatibility; it equals the default now that there is no roster-derived
 * minimum. A parity test asserts it is never stricter than the default.
 */
export const AD_INSTRUMENT_MIN_RESEARCH_QUESTIONS =
  DEFAULT_RESEARCH_QUESTION_FLOOR;

/**
 * The minimum legal research-question count for a draft: always 1.
 *
 * The one owner: the backend runtime (`runtime.ts`) re-exports it. Product
 * decision (2026-08-15): no minimum beyond a single question. The draft
 * argument is kept so call sites stay source-compatible.
 */
export function resolveResearchQuestionFloor(_draft: unknown): number {
  return DEFAULT_RESEARCH_QUESTION_FLOOR;
}

export function addIssue(
  issues: Array<QuestionnaireValidationIssue>,
  path: string,
  code: string,
  message: string,
  severity: QuestionnaireValidationIssue["severity"] = "error",
  params?: QuestionnaireValidationIssue["params"],
): void {
  issues.push({ path, code, message, severity, ...(params ? { params } : {}) });
}

/**
 * The study objective's size rule (rule catalog S50): the decision and its
 * learn bullets share one STUDY_OBJECTIVE_MAX_CHARS budget, and there are at
 * most STUDY_OBJECTIVE_MAX_BULLETS bullets. Nothing cuts an objective to fit:
 * a model's `set_study_goals` over either is refused with these limits, and
 * any other objective over either blocks Review.
 */
export function studyObjectiveIssues(
  goals: unknown,
  path = "studyGoals",
): Array<QuestionnaireValidationIssue> {
  if (!isRecord(goals)) return [];
  const issues: Array<QuestionnaireValidationIssue> = [];
  const decision = typeof goals.decision === "string" ? goals.decision : "";
  const learn = asArray(goals.learn).filter(
    (bullet): bullet is string => typeof bullet === "string",
  );
  const length = learn.reduce(
    (total, bullet) => total + bullet.length,
    decision.length,
  );
  if (length > STUDY_OBJECTIVE_MAX_CHARS) {
    addIssue(
      issues,
      path,
      "study_objective_too_long",
      repairLine("study_objective_too_long", { length, limit: STUDY_OBJECTIVE_MAX_CHARS }),
      "error",
      { length, limit: STUDY_OBJECTIVE_MAX_CHARS },
    );
  }
  if (learn.length > STUDY_OBJECTIVE_MAX_BULLETS) {
    addIssue(
      issues,
      `${path}.learn`,
      "study_objective_too_many_points",
      repairLine("study_objective_too_many_points", { count: learn.length, limit: STUDY_OBJECTIVE_MAX_BULLETS }),
      "error",
      { count: learn.length, limit: STUDY_OBJECTIVE_MAX_BULLETS },
    );
  }
  return issues;
}

export function hasMatrixRowsPlaceholder(question: JsonObject): boolean {
  return asArray(question.placeholderSubstitutions).some(
    (entry) => isRecord(entry) && entry.site === "matrix_rows",
  );
}

export function responseOf(question: JsonObject): JsonObject {
  return isRecord(question.response) ? question.response : {};
}

/** Whether a choice offers enough answer options or grid categories. */
function choiceMemberCount(count: number): Array<RuleFailure> {
  return countWithinBounds({ min: MIN_CHOICE_MEMBERS }, { count });
}

function validateChoiceResponse(
  issues: Array<QuestionnaireValidationIssue>,
  response: JsonObject,
  path: string,
  assetsById: Map<string, JsonObject>,
  wording: PriceWording,
): void {
  const options = asArray(response.options);
  const [tooFewOptions] = choiceMemberCount(options.length);
  if (tooFewOptions?.reason === "below_min") {
    addIssue(
      issues,
      `${path}.options`,
      "choice_options_too_few",
      repairLine("choice_options_too_few", { minValue: String(tooFewOptions.min) }),
    );
  }

  // Semantic hygiene (warning): empty and duplicate option labels confuse
  // respondents. Warnings surface the problem without blocking launch.
  const seenOptionLabels = new Map<string, number>();
  options.forEach((option, optionIndex) => {
    const label =
      isRecord(option) && typeof option.label === "string"
        ? option.label.trim()
        : "";
    if (!label) {
      addIssue(
        issues,
        `${path}.options[${optionIndex}].label`,
        "option_label_empty",
        repairLine("option_label_empty"),
        "warning",
      );
      return;
    }
    const rendered = isRecord(option)
      ? exactChoiceAssetRefAnswerLabel(option, [...assetsById.values()], wording)
      : null;
    const key = (rendered ?? label).toLowerCase();
    const firstIndex = seenOptionLabels.get(key);
    if (firstIndex !== undefined) {
      addIssue(
        issues,
        `${path}.options[${optionIndex}].label`,
        "option_label_duplicate",
        repairLine("option_label_duplicate", { firstIndex: String(firstIndex) }),
        "warning",
      );
    } else {
      seenOptionLabels.set(key, optionIndex);
    }
  });
}

function validateRuntimeRatingUnit(
  issues: Array<QuestionnaireValidationIssue>,
  response: JsonObject,
  path: string,
): void {
  if (
    typeof response.unit === "string" &&
    isRatingUnitId(response.unit) &&
    scaleExecutable({ scales: RATING_SCALES }, { unit: response.unit }).length > 0
  ) {
    addIssue(
      issues,
      `${path}.unit`,
      "rating_scale_not_executable",
      repairLine("rating_scale_not_executable", { supportedUnits: SUPPORTED_RATING_UNIT_IDS }),
    );
  }
}

// Fully-labeled scales: pointLabels is optional, but when present it must be a
// well-formed label-per-point array for the unit. Length mismatches are errors
// (the compiled scale would be ambiguous); blank labels are warnings because the
// compiler degrades them to the numeric value rather than dropping the point.
export function validateRatingPointLabels(
  issues: Array<QuestionnaireValidationIssue>,
  response: JsonObject,
  path: string,
): void {
  const pointLabels = response.pointLabels;
  // null is treated as absent (LLMs emit null for optional fields) — keep in
  // sync with the python and backend-runtime mirrors of this function.
  if (pointLabels === undefined || pointLabels === null) {
    return;
  }
  if (
    !Array.isArray(pointLabels) ||
    pointLabels.some((label) => typeof label !== "string")
  ) {
    addIssue(
      issues,
      `${path}.pointLabels`,
      "point_labels_invalid",
      repairLine("point_labels_invalid"),
    );
    return;
  }
  const unit = typeof response.unit === "string" ? response.unit : "";
  if (unit === "rating_1_7") {
    addIssue(
      issues,
      `${path}.pointLabels`,
      "point_labels_legacy_scale",
      repairLine("point_labels_legacy_scale"),
      "warning",
    );
    return;
  }
  // A scale whose points take no labels (binary_yes_no: the compiler
  // hardcodes Yes/No and the builder drops its labels) is `pointLabels: false`
  // in the scale registry, so accepting labels here would validate a draft
  // the rest of the pipeline silently rewrites.
  const [scaleFailure] = scaleExecutable(
    { scales: RATING_SCALES },
    { unit, pointLabelCount: pointLabels.length },
  );
  if (
    scaleFailure?.reason === "unsupported_scale" ||
    scaleFailure?.reason === "point_labels_not_taken"
  ) {
    addIssue(
      issues,
      `${path}.pointLabels`,
      "point_labels_unsupported_unit",
      repairLine("point_labels_unsupported_unit", { englishList: String(englishList(POINT_LABEL_RATING_UNIT_IDS)) }),
    );
    return;
  }
  if (scaleFailure?.reason === "point_label_count") {
    addIssue(
      issues,
      `${path}.pointLabels`,
      "point_labels_length_mismatch",
      repairLine("point_labels_length_mismatch", { points: String(scaleFailure.points), unit: String(unit), pointLabelsCount: String(pointLabels.length) }),
    );
    return;
  }
  pointLabels.forEach((label, labelIndex) => {
    if (typeof label === "string" && label.trim().length === 0) {
      addIssue(
        issues,
        `${path}.pointLabels[${labelIndex}]`,
        "point_label_empty",
        repairLine("point_label_empty"),
        "warning",
      );
    }
  });
}

export function validateNotApplicableLabel(
  issues: Array<QuestionnaireValidationIssue>,
  response: JsonObject,
  path: string,
): void {
  const label = response.notApplicableLabel;
  if (label === undefined || label === null) return;
  if (typeof label !== "string" || label.trim().length === 0) {
    addIssue(
      issues,
      `${path}.notApplicableLabel`,
      "not_applicable_label_invalid",
      repairLine("not_applicable_label_invalid"),
    );
    return;
  }
  if (response.includeNotApplicable !== true) {
    addIssue(
      issues,
      `${path}.notApplicableLabel`,
      "not_applicable_label_without_option",
      repairLine("not_applicable_label_without_option"),
    );
  }
}

type MatrixAssetContext = {
  declaredAssetTypes: Set<string>;
  testedAssets: Array<JsonObject>;
};

function validateMatrixRows(
  issues: Array<QuestionnaireValidationIssue>,
  response: JsonObject,
  path: string,
  matrixAssetContext?: MatrixAssetContext,
): void {
  const rows = isRecord(response.rows) ? response.rows : {};
  const source = rows.source;
  // Any other source is the closed shape's (`matrixRows`) to refuse.
  if (source !== "fixed" && source !== "tested_assets") return;

  asArray(rows.rows).forEach((row, rowIndex) => {
    if (isRecord(row)) {
      validateLabelLength(
        issues,
        row.label,
        `${path}.rows.rows[${rowIndex}].label`,
        "This row label",
      );
    }
  });
  if (source === "fixed" && asArray(rows.rows).length < 1) {
    addIssue(
      issues,
      `${path}.rows.rows`,
      "matrix_rows_too_few",
      repairLine("matrix_rows_too_few"),
    );
  }

  if (source === "fixed") {
    // A blank row label is a transient edit state in the builder; flag it so the
    // user fills or removes it before launch (mirrors matrix_measure_label_missing
    // / choice option label checks). The compiler also drops blank rows.
    asArray(rows.rows).forEach((row: any, rowIndex: number) => {
      const label = typeof row?.label === "string" ? row.label.trim() : "";
      if (!label) {
        addIssue(
          issues,
          `${path}.rows.rows[${rowIndex}].label`,
          "matrix_row_label_empty",
          repairLine("matrix_row_label_empty"),
          "warning",
        );
      }
    });
  }

  if (source === "tested_assets" && typeof rows.assetType !== "string") {
    addIssue(
      issues,
      `${path}.rows.assetType`,
      "matrix_asset_type_required",
      repairLine("matrix_asset_type_required"),
    );
    return;
  }

  if (
    source === "tested_assets" &&
    typeof rows.assetType === "string" &&
    matrixAssetContext
  ) {
    const assetType = rows.assetType;
    if (!matrixAssetContext.declaredAssetTypes.has(assetType)) {
      addIssue(
        issues,
        `${path}.rows.assetType`,
        "matrix_asset_type_undeclared",
        repairLine("matrix_asset_type_undeclared", { assetType: String(assetType), value: String([
          ...matrixAssetContext.declaredAssetTypes,
        ].join(", ")) }),
      );
    }

    const matchingAssets = matrixAssetContext.testedAssets.filter(
      (asset) => isRecord(asset) && asset.type === assetType,
    );
    // A tested-assets matrix has no authored `rows.rows`: its row labels are
    // the asset names, resolved later, so the guard reads the names.
    matchingAssets.forEach((asset, assetIndex) => {
      validateLabelLength(
        issues,
        asset.name,
        `${path}.rows.assetType[${assetIndex}].name`,
        "This asset name, used as a matrix row label,",
      );
    });
    if (matchingAssets.length === 0) {
      const availableTypes = [
        ...new Set(
          matrixAssetContext.testedAssets
            .filter(
              (asset): asset is JsonObject =>
                isRecord(asset) && typeof asset.type === "string",
            )
            .map((asset) => asset.type as string),
        ),
      ];
      addIssue(
        issues,
        `${path}.rows.assetType`,
        "matrix_rows_no_matching_assets",
        repairLine("matrix_rows_no_matching_assets", { assetType: String(assetType), availableTypes: String(availableTypes.length > 0 ? availableTypes.join(", ") : "none") }),
      );
    }
  }
}

export function collectStableKey(
  issues: Array<QuestionnaireValidationIssue>,
  seenStableKeys: Map<string, string>,
  stableKey: unknown,
  path: string,
): void {
  if (typeof stableKey !== "string") {
    return;
  }

  const firstPath = seenStableKeys.get(stableKey);
  if (firstPath) {
    addIssue(
      issues,
      path,
      "duplicate_stable_key",
      repairLine("duplicate_stable_key", { firstPath: String(firstPath) }),
    );
    return;
  }

  seenStableKeys.set(stableKey, path);
}

function validateQuestion(
  issues: Array<QuestionnaireValidationIssue>,
  question: JsonObject,
  questionPath: string,
  wording: PriceWording,
  matrixAssetContext?: MatrixAssetContext,
  assetsById: Map<string, JsonObject> = new Map(),
): void {
  const type = question.type;
  const response = responseOf(question);
  const responsePath = `${questionPath}.response`;

  if (hasMatrixRowsPlaceholder(question)) {
    addIssue(
      issues,
      questionPath,
      "matrix_rows_placeholder",
      repairLine("matrix_rows_placeholder"),
    );
  }

  if (valueInList({ values: QUESTION_TYPE_IDS }, { value: type }).length > 0) {
    addIssue(
      issues,
      `${questionPath}.type`,
      "unsupported_question_type",
      repairLine("unsupported_question_type", { type: String(String(type)) }),
    );
    return;
  }

  if (response.kind !== type) {
    addIssue(
      issues,
      `${responsePath}.kind`,
      "response_kind_mismatch",
      repairLine("response_kind_mismatch"),
    );
  }

  if (
    typeof question.title === "string" &&
    respondentTextGluesQuestionStems(question.title)
  ) {
    addIssue(
      issues,
      `${questionPath}.title`,
      "question_stem_glued",
      repairLine("question_stem_glued", { field: "title", form: "stem" }),
    );
  }
  if (
    typeof question.description === "string" &&
    respondentTextGluesQuestionStems(question.description)
  ) {
    addIssue(
      issues,
      `${questionPath}.description`,
      "question_stem_glued",
      repairLine("question_stem_glued", { field: "description", form: "instruction" }),
    );
  }

  if (type === "single_select" || type === "multi_select") {
    validateChoiceResponse(issues, response, responsePath, assetsById, wording);
  }

  if (type === "ranking") {
    const options = asArray(response.options);
    const [tooFewOptions] = choiceMemberCount(options.length);
    if (tooFewOptions?.reason === "below_min") {
      addIssue(
        issues,
        `${responsePath}.options`,
        "ranking_options_too_few",
        repairLine("ranking_options_too_few", { minValue: String(tooFewOptions.min) }),
      );
    }
  }

  if (type === "rating") {
    validateRuntimeRatingUnit(issues, response, responsePath);
    validateRatingPointLabels(issues, response, responsePath);
    validateNotApplicableLabel(issues, response, responsePath);
  }

  if (
    type === "continuous" &&
    response.precision != null &&
    (!Number.isInteger(response.precision) || Number(response.precision) < 0)
  ) {
    addIssue(
      issues,
      `${responsePath}.precision`,
      "continuous_precision_invalid",
      repairLine("continuous_precision_invalid"),
    );
  }

  if (type === "continuous" && response.unit === "duration") {
    if (typeof response.durationUnit !== "string") {
      addIssue(
        issues,
        `${responsePath}.durationUnit`,
        "duration_unit_required",
        repairLine("duration_unit_required"),
      );
    } else if (
      !DURATION_EXPOSED_UNITS.includes(response.durationUnit as never)
    ) {
      addIssue(
        issues,
        `${responsePath}.durationUnit`,
        "unsupported_duration_unit",
        repairLine("unsupported_duration_unit", { englishList: String(englishList(DURATION_EXPOSED_UNITS)) }),
      );
    }
  }

  if (type === "matrix_rating") {
    validateMatrixRows(issues, response, responsePath, matrixAssetContext);
    const measures = asArray(response.measures);
    if (measures.length < 1) {
      addIssue(
        issues,
        `${responsePath}.measures`,
        "matrix_measures_too_few",
        repairLine("matrix_measures_too_few"),
      );
    }
    const [tooManyMeasures] = countWithinBounds(
      { max: MAX_MATRIX_MEASURES },
      { count: measures.length },
    );
    if (tooManyMeasures?.reason === "above_max") {
      addIssue(
        issues,
        `${responsePath}.measures`,
        "matrix_measures_too_many",
        repairLine("matrix_measures_too_many", { maxValue: String(tooManyMeasures.max), measuresCount: String(measures.length) }),
      );
    }
    // All measures in one matrix should share a single rating scale — mixing
    // e.g. 0-10 with 1-5 makes the columns incomparable, and stamping and
    // the report both read one scale per grid, so it blocks.
    const measureUnits = new Set<string>();
    measures.forEach((measure) => {
      if (isRecord(measure) && typeof measure.unit === "string") {
        measureUnits.add(measure.unit);
      }
    });
    if (measureUnits.size > 1) {
      addIssue(
        issues,
        `${responsePath}.measures`,
        "matrix_measures_mixed_units",
        repairLine("matrix_measures_mixed_units"),
        "error",
      );
    }

    measures.forEach((measure, measureIndex) => {
      // A measure that is not an object is the closed shape's to refuse.
      if (!isRecord(measure)) return;
      validateLabelLength(
        issues,
        measure.label,
        `${responsePath}.measures[${measureIndex}].label`,
        "This measure label",
      );

      validateRuntimeRatingUnit(
        issues,
        measure,
        `${responsePath}.measures[${measureIndex}]`,
      );
      validateRatingPointLabels(
        issues,
        measure,
        `${responsePath}.measures[${measureIndex}]`,
      );
      validateNotApplicableLabel(
        issues,
        measure,
        `${responsePath}.measures[${measureIndex}]`,
      );

      // An unlabeled measure renders as a blank/phantom column (warning).
      const measureLabel =
        typeof measure.label === "string" ? measure.label.trim() : "";
      if (!measureLabel) {
        addIssue(
          issues,
          `${responsePath}.measures[${measureIndex}].label`,
          "matrix_measure_label_missing",
          repairLine("matrix_measure_label_missing"),
          "warning",
        );
      }
    });
  }

  if (type === "matrix_categorical") {
    validateMatrixRows(issues, response, responsePath, matrixAssetContext);
    const categories = asArray(response.categories);
    const [tooFewCategories] = choiceMemberCount(categories.length);
    if (tooFewCategories?.reason === "below_min") {
      addIssue(
        issues,
        `${responsePath}.categories`,
        "matrix_categories_too_few",
        repairLine("matrix_categories_too_few", { minValue: String(tooFewCategories.min) }),
      );
    }
    categories.forEach((category, categoryIndex) => {
      if (isRecord(category)) {
        validateLabelLength(
          issues,
          category.label,
          `${responsePath}.categories[${categoryIndex}].label`,
          "This category label",
        );
      }
    });
    const [tooManyCategories] = countWithinBounds(
      { max: MAX_MATRIX_CATEGORIES },
      { count: categories.length },
    );
    if (tooManyCategories?.reason === "above_max") {
      addIssue(
        issues,
        `${responsePath}.categories`,
        "matrix_categories_too_many",
        repairLine("matrix_categories_too_many", { maxValue: String(tooManyCategories.max), categoriesCount: String(categories.length) }),
      );
    }
  }
}

function validateRecipeModuleQuestionType(
  issues: Array<QuestionnaireValidationIssue>,
  question: JsonObject,
  questionPath: string,
  payload: JsonObject,
): void {
  const mismatch = recipeModuleTypeMismatchOf(payload, question);
  if (!mismatch) {
    return;
  }
  addIssue(
    issues,
    `${questionPath}.type`,
    "question_recipe_module_type_mismatch",
    repairLine("question_recipe_module_type_mismatch", {
      actual: mismatch.actual,
      moduleId: mismatch.moduleId,
      expected: mismatch.expected,
    }),
  );
}

export function validateAssetBindings(
  issues: Array<QuestionnaireValidationIssue>,
  question: JsonObject,
  questionPath: string,
  declaredAssetTypes: Set<string>,
): void {
  if (!isRecord(question.assetBinding)) {
    return;
  }

  asArray(question.assetBinding.assetTypes).forEach(
    (assetType, assetTypeIndex) => {
      if (typeof assetType !== "string" || !declaredAssetTypes.has(assetType)) {
        addIssue(
          issues,
          `${questionPath}.assetBinding.assetTypes[${assetTypeIndex}]`,
          "asset_binding_type_missing",
          repairLine("asset_binding_type_missing"),
        );
      }
    },
  );
}

export function hasText(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

export function hasGcsImageReference(mediaRef: JsonObject): boolean {
  if (hasText(mediaRef.gcsUri) && mediaRef.gcsUri.trim().startsWith("gs://")) {
    return true;
  }
  if (hasText(mediaRef.bucket) && hasText(mediaRef.objectKey)) {
    return true;
  }
  const publicUrl = hasText(mediaRef.publicUrl)
    ? mediaRef.publicUrl
    : hasText(mediaRef.url)
      ? mediaRef.url
      : hasText(mediaRef.filePath)
        ? mediaRef.filePath
        : "";
  return /^https:\/\/storage\.googleapis\.com\/[^/]+\/.+/i.test(
    publicUrl.trim(),
  );
}

function validateTestedAssets(
  issues: Array<QuestionnaireValidationIssue>,
  testedAssets: JsonObject,
  seenStableKeys: Map<string, string>,
): {
  assetIds: Set<string>;
  assetTypes: Set<string>;
  assetsById: Map<string, JsonObject>;
} {
  const assetTypes = new Set(
    asArray(testedAssets.assetTypes).filter(
      (assetType): assetType is string => typeof assetType === "string",
    ),
  );
  const assetIds = new Set<string>();
  const assetsById = new Map<string, JsonObject>();
  const stableKeys = new Set<string>();
  const required = testedAssets.required === true;

  const sharedContext = testedAssets.sharedContext;
  if (sharedContext !== undefined) {
    const contextPath = "testedAssets.sharedContext";
    if (!isRecord(sharedContext)) {
      addIssue(
        issues,
        contextPath,
        "asset_shared_context_invalid",
        worded("asset_shared_context_invalid", "Shared asset context must carry literal source provenance."),
      );
    } else {
      const provenance = isRecord(sharedContext.provenance)
        ? sharedContext.provenance
        : {};
      const segments = asArray(provenance.segments);
      const completeSegments = segments.filter(
        (segment) =>
          isRecord(segment) &&
          hasText(segment.sourceId) &&
          typeof segment.text === "string" &&
          segment.text.trim().length > 0,
      ) as Array<JsonObject>;
      const assembled = completeSegments
        .map((segment) => segment.text as string)
        .join("\n");
      if (
        sharedContext.schemaVersion !== "tested-asset-shared-context.v1" ||
        !hasText(sharedContext.text) ||
        provenance.kind !== "literal_source_segments" ||
        segments.length === 0 ||
        completeSegments.length !== segments.length ||
        assembled !== sharedContext.text
      ) {
        addIssue(
          issues,
          contextPath,
          "asset_shared_context_invalid",
          worded("asset_shared_context_invalid", "Shared asset context must equal its ordered, non-empty literal source segments."),
        );
      } else if (!isWithinAssetDescriptionWordLimit(sharedContext.text)) {
        addIssue(
          issues,
          `${contextPath}.text`,
          "asset_shared_context_too_long",
          repairLine("asset_shared_context_too_long", { toLocaleString: String(MAX_ASSET_DESCRIPTION_WORDS.toLocaleString()) }),
        );
      } else if (asArray(testedAssets.assets).length === 0) {
        addIssue(
          issues,
          contextPath,
          "asset_shared_context_without_assets",
          repairLine("asset_shared_context_without_assets"),
        );
      }
    }
  }

  asArray(testedAssets.assets).forEach((asset, index) => {
    if (!isRecord(asset)) {
      addIssue(
        issues,
        `testedAssets.assets[${index}]`,
        "asset_invalid",
        repairLine("asset_invalid"),
      );
      return;
    }

    const path = `testedAssets.assets[${index}]`;
    ["id", "type", "stableKey", "name"].forEach((field) => {
      if (!hasText(asset[field])) {
        addIssue(
          issues,
          `${path}.${field}`,
          "asset_required_field_missing",
          worded("asset_required_field_missing", `Asset requires ${field}.`),
        );
      }
    });

    if (hasText(asset.id)) {
      assetIds.add(asset.id);
      assetsById.set(asset.id, asset);
    }

    if (hasText(asset.stableKey)) {
      collectStableKey(
        issues,
        seenStableKeys,
        asset.stableKey,
        `${path}.stableKey`,
      );
      if (stableKeys.has(asset.stableKey)) {
        addIssue(
          issues,
          `${path}.stableKey`,
          "duplicate_asset_stable_key",
          worded("duplicate_asset_stable_key", "Asset stable keys must be unique within a draft."),
        );
      }
      stableKeys.add(asset.stableKey);
    }

    // The registry says which families need a description (QA4 AD N4).
    if (
      isAssetFamily(asset.type) &&
      DESCRIPTION_REQUIRED_FAMILIES.has(asset.type) &&
      required &&
      !hasText(asset.description)
    ) {
      addIssue(
        issues,
        `${path}.description`,
        "concept_description_required",
        repairLine("concept_description_required", { assetFamilyTitleNoun: String(assetFamilyTitleNoun(asset.type)) }),
      );
    }

    if (
      hasText(asset.description) &&
      !isWithinAssetDescriptionWordLimit(asset.description)
    ) {
      addIssue(
        issues,
        `${path}.description`,
        "asset_description_too_long",
        repairLine("asset_description_too_long", { toLocaleString: String(MAX_ASSET_DESCRIPTION_WORDS.toLocaleString()) }),
      );
    }

    // The family's other required fields (`requiredFields`: a message's
    // text) must be non-empty (warning — surfaces an incomplete asset without
    // blocking existing drafts).
    const familyRequires = (field: string): boolean =>
      isAssetFamily(asset.type) &&
      ASSET_FAMILY_FACTS[asset.type].requiredFields.includes(field);
    if (familyRequires("messageText") && required && !hasText(asset.messageText)) {
      addIssue(
        issues,
        `${path}.messageText`,
        "message_text_required",
        worded("message_text_required", "Message assets require the claim text (messageText)."),
        "warning",
      );
    }


    asArray(asset.media).forEach((mediaRef, mediaIndex) => {
      if (!isRecord(mediaRef)) {
        addIssue(
          issues,
          `${path}.media[${mediaIndex}]`,
          "asset_media_invalid",
          repairLine("asset_media_invalid"),
        );
        return;
      }
      if (mediaRef.kind === "image" && !hasGcsImageReference(mediaRef)) {
        addIssue(
          issues,
          `${path}.media[${mediaIndex}]`,
          "asset_image_gcs_reference_required",
          repairLine("asset_image_gcs_reference_required"),
        );
      }
      const unreadableVideo = nonKeyframeableVideoIssue(mediaRef);
      if (unreadableVideo) {
        addIssue(
          issues,
          `${path}.media[${mediaIndex}]`,
          "asset_video_not_keyframeable",
          worded("asset_video_not_keyframeable", unreadableVideo),
        );
      }
    });
  });

  return { assetIds, assetTypes, assetsById };
}

/**
 * Why twins cannot watch this video, or null. Twins need ffmpeg keyframes,
 * which a YouTube link or a player-page host (Vimeo, Drive, Dropbox) cannot
 * give: the one rule readiness, compile and launch read (QA4 A2 #10). An
 * uploaded file (`gs://`, `data:`) or no link at all is not this rule's.
 */
function nonKeyframeableVideoIssue(mediaRef: JsonObject): string | null {
  if (mediaRef.kind !== "video") return null;
  const url = String(mediaRef.publicUrl || mediaRef.url || "").trim();
  if (mediaRef.mimeType === "video/youtube") {
    return nonKeyframeableVideoMessage(url) ?? YOUTUBE_NON_KEYFRAMEABLE_MESSAGE;
  }
  if (!url || url.startsWith("data:") || url.startsWith("gs://")) return null;
  return nonKeyframeableVideoMessage(url);
}

export function validateAssetRefs(
  issues: Array<QuestionnaireValidationIssue>,
  owner: JsonObject,
  ownerPath: string,
  assetsById: Map<string, JsonObject>,
): void {
  asArray(owner.assetRefs).forEach((assetRef, assetRefIndex) => {
    const path = `${ownerPath}.assetRefs[${assetRefIndex}]`;
    if (!isRecord(assetRef)) {
      addIssue(
        issues,
        path,
        "asset_ref_invalid",
        repairLine("asset_ref_invalid"),
      );
      return;
    }

    const assetId = hasText(assetRef.assetId) ? assetRef.assetId : "";
    const asset = assetId ? assetsById.get(assetId) : undefined;
    if (!assetId || !asset) {
      addIssue(
        issues,
        `${path}.assetId`,
        "asset_ref_missing",
        worded("asset_ref_missing", "Question asset references must point to an existing tested asset."),
      );
      return;
    }

    if (
      hasText(assetRef.assetStableKey) &&
      assetRef.assetStableKey !== asset.stableKey
    ) {
      addIssue(
        issues,
        `${path}.assetStableKey`,
        "asset_ref_stable_key_mismatch",
        worded("asset_ref_stable_key_mismatch", "Question asset reference stable key must match the tested asset."),
      );
    }
    if (hasText(assetRef.assetType) && assetRef.assetType !== asset.type) {
      addIssue(
        issues,
        `${path}.assetType`,
        "asset_ref_type_mismatch",
        worded("asset_ref_type_mismatch", "Question asset reference type must match the tested asset."),
      );
    }
  });
}

function validateChoiceOptionAssetRefs(
  issues: Array<QuestionnaireValidationIssue>,
  response: JsonObject,
  responsePath: string,
  assetsById: Map<string, JsonObject>,
): void {
  asArray(response.options).forEach((option, optionIndex) => {
    if (isRecord(option)) {
      validateAssetRefs(
        issues,
        option,
        `${responsePath}.options[${optionIndex}]`,
        assetsById,
      );
      // Choice options render as `bars[].label` on the single-select,
      // multi-select and ranking slides; this walker visits every option.
      validateLabelLength(
        issues,
        option.label,
        `${responsePath}.options[${optionIndex}].label`,
        "This option label",
      );
    }
  });
  asArray(response.categories).forEach((category, categoryIndex) => {
    if (isRecord(category)) {
      validateAssetRefs(
        issues,
        category,
        `${responsePath}.categories[${categoryIndex}]`,
        assetsById,
      );
    }
  });
}

export function collectCompositeMetricIds(draft: JsonObject): Set<string> {
  const metricPlan = isRecord(draft.metricPlan) ? draft.metricPlan : {};
  const compositeMetricIds = new Set<string>();

  asArray(metricPlan.compositeMetrics).forEach((compositeMetric) => {
    if (!isRecord(compositeMetric)) {
      return;
    }

    asArray(compositeMetric.components).forEach((component) => {
      if (isRecord(component) && typeof component.metricId === "string") {
        compositeMetricIds.add(component.metricId);
      }
    });
  });

  return compositeMetricIds;
}

export function collectMetricIds(draft: JsonObject): Set<string> {
  const metricPlan = isRecord(draft.metricPlan) ? draft.metricPlan : {};
  const metricIds = new Set<string>();
  asArray(metricPlan.metrics).forEach((metric) => {
    if (isRecord(metric) && typeof metric.id === "string") {
      metricIds.add(metric.id);
    }
  });
  return metricIds;
}

export function validateCompositeReferences(
  issues: Array<QuestionnaireValidationIssue>,
  draft: JsonObject,
): void {
  const metricPlan = isRecord(draft.metricPlan) ? draft.metricPlan : {};
  const composites = asArray(metricPlan.compositeMetrics);
  if (composites.length === 0) {
    return;
  }

  const metricIds = collectMetricIds(draft);

  // T4 creates these declared classifier outputs at scoring time. Keep this
  // composite-only: they are not ordinary question or slide metric references.
  for (const analysis of asArray(metricPlan.qualitativeAnalyses)) {
    if (!isRecord(analysis) || typeof analysis.stableKey !== "string") {
      continue;
    }
    const classifier = analysis.classifier;
    if (
      isRecord(classifier) &&
      typeof classifier.metricKind === "string" &&
      classifier.metricKind.length > 0
    ) {
      metricIds.add(
        openEndClassifierMetricId(analysis.stableKey, classifier.metricKind),
      );
    }
  }

  composites.forEach((composite, compositeIndex) => {
    if (!isRecord(composite)) {
      return;
    }
    const compositePath = `metricPlan.compositeMetrics[${compositeIndex}]`;
    const components = asArray(composite.components);
    let liveComponents = 0;

    components.forEach((component, componentIndex) => {
      if (!isRecord(component) || typeof component.metricId !== "string") {
        return;
      }
      if (metricIds.has(component.metricId)) {
        liveComponents += 1;
        return;
      }
      addIssue(
        issues,
        `${compositePath}.components[${componentIndex}].metricId`,
        "composite_metric_ref_missing",
        repairLine("composite_metric_ref_missing"),
        "warning",
      );
    });

    if (components.length > 0 && liveComponents === 0) {
      addIssue(
        issues,
        compositePath,
        "composite_has_no_live_components",
        repairLine("composite_has_no_live_components"),
        "warning",
      );
    }
  });
}

export function validateCompositeBounds(
  issues: Array<QuestionnaireValidationIssue>,
  question: JsonObject,
  questionPath: string,
  compositeMetricIds: Set<string>,
): void {
  const response = responseOf(question);
  const unit = typeof response.unit === "string" ? response.unit : undefined;
  if (!unit || !unitRequiresExplicitBoundsForComposite(unit)) {
    return;
  }

  const participatesInComposite = asArray(question.metricRefs).some(
    (metricRef) =>
      typeof metricRef === "string" && compositeMetricIds.has(metricRef),
  );
  if (!participatesInComposite) {
    return;
  }

  if (typeof response.min !== "number" || typeof response.max !== "number") {
    addIssue(
      issues,
      `${questionPath}.response`,
      "composite_bounds_required",
      repairLine("composite_bounds_required"),
    );
  }
}

export function validateOpenEndCoverage(
  issues: Array<QuestionnaireValidationIssue>,
  draft: JsonObject,
  question: JsonObject,
  questionPath: string,
): void {
  if (question.type !== "open_end") {
    return;
  }

  const questionId = typeof question.id === "string" ? question.id : "";
  const metricPlan = isRecord(draft.metricPlan) ? draft.metricPlan : {};
  const qualitativeAnalyses = asArray(metricPlan.qualitativeAnalyses);
  const hasQualAnalysis = qualitativeAnalyses.some(
    (analysis) => isRecord(analysis) && analysis.questionId === questionId,
  );
  if (!hasQualAnalysis) {
    addIssue(
      issues,
      `${questionPath}.metricRefs`,
      "open_end_qual_missing",
      repairLine("open_end_qual_missing"),
    );
  }
  // The slide builder gives a Survey-Data-only question no slide.
  if (
    isSurveyDataOnlyQuestion(
      question as Parameters<typeof isSurveyDataOnlyQuestion>[0],
    )
  ) {
    return;
  }

  const slidePlan = isRecord(draft.slidePlan) ? draft.slidePlan : {};
  const ordering = asArray(slidePlan.ordering);
  const openEndSlideTypes = new Set(
    ordering
      .filter(
        (slide) =>
          isRecord(slide) && asArray(slide.questionIds).includes(questionId),
      )
      .map((slide) => (isRecord(slide) ? slide.slideType : undefined)),
  );

  const frame = isRecord(question.planAttribution)
    ? question.planAttribution.openEndAnalysisFrame
    : undefined;
  const expectedSlideTypes =
    frame === "factual"
      ? ["qual_descriptive_themes"]
      : ["qual_positive_themes", "qual_negative_themes"];

  const actualFrameSlideTypes = new Set<string>(
    [...openEndSlideTypes].filter(isQualitativeSlideType),
  );
  if (
    actualFrameSlideTypes.size !== expectedSlideTypes.length ||
    !expectedSlideTypes.every((slideType) => actualFrameSlideTypes.has(slideType))
  ) {
    addIssue(
      issues,
      `${questionPath}.slideRefs`,
      "open_end_slides_missing",
      worded("open_end_slides_missing", frame === "factual"
        ? "Factual open-end questions require only a descriptive qualitative slide."
        : "Evaluative open-end questions require only positive and negative qual slides."),
    );
  }
}

export function collectMatrixVisibilityRowValues(
  response: JsonObject,
  assetsById: Map<string, JsonObject>,
): Set<string> {
  const rows = isRecord(response.rows) ? response.rows : {};
  if (rows.source === "fixed") {
    return new Set(
      asArray(rows.rows)
        .filter(isRecord)
        .map((row) => row.value)
        .filter(
          (value): value is string =>
            typeof value === "string" && value.length > 0,
        ),
    );
  }
  if (rows.source === "tested_assets" && typeof rows.assetType === "string") {
    const assetType = rows.assetType;
    return new Set(
      [...assetsById.values()]
        .filter(
          (asset) => asset.type === assetType && typeof asset.id === "string",
        )
        .map((asset) => asset.id as string),
    );
  }
  return new Set();
}

function validateMatrixRatingVisibilityRule(
  issues: Array<QuestionnaireValidationIssue>,
  rulePath: string,
  fieldLabel: string,
  response: JsonObject,
  threshold: number | null,
  matrixRowValue: unknown,
  matrixMeasureId: unknown,
  hasMatrixRowValue: boolean,
  hasMatrixMeasureId: boolean,
  assetsById: Map<string, JsonObject>,
  thresholdHigh?: number,
  requireNotApplicable = false,
): void {
  if (
    !hasMatrixRowValue ||
    typeof matrixRowValue !== "string" ||
    !matrixRowValue
  ) {
    addIssue(
      issues,
      `${rulePath}.matrixRowValue`,
      "visibility_rule_unknown_matrix_row",
      worded("visibility_rule_unknown_matrix_row", `${fieldLabel}.matrixRowValue is required for matrix_rating dependencies.`),
    );
    return;
  }

  const allowedRows = collectMatrixVisibilityRowValues(response, assetsById);
  if (!allowedRows.has(matrixRowValue)) {
    addIssue(
      issues,
      `${rulePath}.matrixRowValue`,
      "visibility_rule_unknown_matrix_row",
      worded("visibility_rule_unknown_matrix_row", `${fieldLabel}.matrixRowValue must match a row on the referenced matrix_rating question: ${matrixRowValue}.`),
    );
  }

  const measures = asArray(response.measures).filter(isRecord);
  if (measures.length === 0) {
    addIssue(
      issues,
      `${rulePath}.dependsOnStableKey`,
      "visibility_rule_source_incomplete",
      worded("visibility_rule_source_incomplete", `${fieldLabel} matrix_rating dependency must have at least one measure.`),
    );
    return;
  }

  let selectedMeasure: JsonObject | null = null;
  if (measures.length === 1) {
    const soleMeasure = measures[0];
    if (
      hasMatrixMeasureId &&
      (typeof matrixMeasureId !== "string" ||
        matrixMeasureId !== soleMeasure.id)
    ) {
      addIssue(
        issues,
        `${rulePath}.matrixMeasureId`,
        "visibility_rule_unknown_matrix_measure",
        worded("visibility_rule_unknown_matrix_measure", `${fieldLabel}.matrixMeasureId must match the sole measure id (or be omitted) for single-measure matrix_rating dependencies.`),
      );
      return;
    }
    selectedMeasure = soleMeasure;
  } else {
    if (
      !hasMatrixMeasureId ||
      typeof matrixMeasureId !== "string" ||
      !matrixMeasureId
    ) {
      addIssue(
        issues,
        `${rulePath}.matrixMeasureId`,
        "visibility_rule_unknown_matrix_measure",
        worded("visibility_rule_unknown_matrix_measure", `${fieldLabel}.matrixMeasureId is required for multi-measure matrix_rating dependencies.`),
      );
      return;
    }
    selectedMeasure =
      measures.find((measure) => measure.id === matrixMeasureId) ?? null;
    if (!selectedMeasure) {
      addIssue(
        issues,
        `${rulePath}.matrixMeasureId`,
        "visibility_rule_unknown_matrix_measure",
        worded("visibility_rule_unknown_matrix_measure", `${fieldLabel}.matrixMeasureId must match a measure on the referenced matrix_rating question: ${matrixMeasureId}.`),
      );
      return;
    }
  }

  if (requireNotApplicable && selectedMeasure.includeNotApplicable !== true) {
    addIssue(
      issues,
      `${rulePath}.matrixMeasureId`,
      "visibility_rule_unsupported_dependency_type",
      worded("visibility_rule_unsupported_dependency_type", `${fieldLabel} is_not_applicable requires a matrix_rating measure with includeNotApplicable.`),
    );
    return;
  }

  const unit = selectedMeasure.unit;
  if (typeof unit !== "string" || !isRatingUnitId(unit)) {
    addIssue(
      issues,
      `${rulePath}.dependsOnStableKey`,
      "visibility_rule_source_incomplete",
      worded("visibility_rule_source_incomplete", `${fieldLabel} matrix_rating dependency measure must have a valid rating unit.`),
    );
    return;
  }
  if (threshold === null) {
    return;
  }
  const [min, max] = ratingBounds(unit);
  if (threshold < min || threshold > max) {
    addIssue(
      issues,
      `${rulePath}.threshold`,
      "visibility_rule_threshold_out_of_bounds",
      worded("visibility_rule_threshold_out_of_bounds", `${fieldLabel}.threshold must be between ${min} and ${max} for unit ${unit}.`),
    );
  }
  if (
    typeof thresholdHigh === "number" &&
    (thresholdHigh < min || thresholdHigh > max)
  ) {
    addIssue(
      issues,
      `${rulePath}.thresholdHigh`,
      "visibility_rule_threshold_out_of_bounds",
      worded("visibility_rule_threshold_out_of_bounds", `${fieldLabel}.thresholdHigh must be between ${min} and ${max} for unit ${unit}.`),
    );
  }
}

function validateMatrixCategoricalVisibilityRule(
  issues: Array<QuestionnaireValidationIssue>,
  rulePath: string,
  fieldLabel: string,
  response: JsonObject,
  matrixRowValue: unknown,
  hasMatrixRowValue: boolean,
  assetsById: Map<string, JsonObject>,
): void {
  if (!hasMatrixRowValue || typeof matrixRowValue !== "string" || !matrixRowValue) {
    addIssue(issues, `${rulePath}.matrixRowValue`, "visibility_rule_unknown_matrix_row", worded("visibility_rule_unknown_matrix_row", `${fieldLabel}.matrixRowValue is required for matrix_categorical dependencies.`));
    return;
  }
  if (!collectMatrixVisibilityRowValues(response, assetsById).has(matrixRowValue)) {
    addIssue(issues, `${rulePath}.matrixRowValue`, "visibility_rule_unknown_matrix_row", worded("visibility_rule_unknown_matrix_row", `${fieldLabel}.matrixRowValue must match a row on the referenced matrix_categorical question: ${matrixRowValue}.`));
  }
}

export function isVisibilityRuleGroupRecord(rule: unknown): boolean {
  return (
    isRecord(rule) &&
    (rule.join === "and" || rule.join === "or") &&
    Array.isArray(rule.rules)
  );
}

const CHOICES_FROM_MODES = new Set(["selected", "unselected", "all"]);
// Which question types carry answers forward (`choicesFromSource`), and which
// receive them (`choicesFromTarget`): question-capability registry flags.
const QUESTION_CAPABILITY_FACTS = { capabilities: QUESTION_CAPABILITIES };

function optionValuesOf(response: JsonObject): Set<string> {
  return new Set(
    asArray(response.options)
      .filter(isRecord)
      .map((option) => (typeof option.value === "string" ? option.value : ""))
      .filter(Boolean),
  );
}

interface ChoiceOptionMirrorMetadata {
  label: unknown;
  exclusive: boolean;
}

function optionMirrorMetadataOf(
  response: JsonObject,
): Map<string, ChoiceOptionMirrorMetadata> {
  const metadata = new Map<string, ChoiceOptionMirrorMetadata>();
  asArray(response.options)
    .filter(isRecord)
    .forEach((option) => {
      if (typeof option.value !== "string" || !option.value) {
        return;
      }
      metadata.set(option.value, {
        label: option.label,
        exclusive: option.exclusive === true,
      });
    });
  return metadata;
}

function positiveInteger(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) && value > 0
    ? value
    : fallback;
}

function nonNegativeInteger(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0
    ? value
    : fallback;
}

function choicesFromTargetMinimum(
  question: JsonObject,
  response: JsonObject,
  kind: string,
): { count: number; pathSuffix: string } {
  if (kind === "multi_select") {
    return {
      count: positiveInteger(
        response.minSelected,
        question.required === true ? 1 : 0,
      ),
      pathSuffix: "minSelected",
    };
  }
  if (kind === "ranking") {
    const bounds = rankingBounds(response);
    return {
      count:
        bounds.kind === "partial"
          ? bounds.min
          : question.required === true
            ? 1
            : 0,
      pathSuffix: "minRankedItems",
    };
  }
  return { count: 1, pathSuffix: "choicesFrom" };
}

/**
 * Smallest non-empty choice set SurveyJS can expose to the target for any
 * valid source answer. Empty carry-forward sets hide the target, so they do
 * not make its minimum impossible.
 */
function minimumVisibleChoicesFromCount(
  source: JsonObject,
  sourceResponse: JsonObject,
  sourceKind: string,
  mode: string,
  optionCount: number,
): number | null {
  if (optionCount < 1) {
    return null;
  }
  if (mode === "all") {
    return optionCount;
  }
  if (sourceKind === "single_select") {
    const count = mode === "selected" ? 1 : optionCount - 1;
    return count > 0 ? count : null;
  }

  const minimumSelected = positiveInteger(sourceResponse.minSelected, 1);
  const maximumSelected = Math.min(
    nonNegativeInteger(sourceResponse.maxSelected, optionCount),
    optionCount,
  );
  if (mode === "selected") {
    return minimumSelected <= maximumSelected && minimumSelected <= optionCount
      ? minimumSelected
      : null;
  }

  const visibleCounts: Array<number> = [];
  const largestSelectionWithRemainder = Math.min(
    maximumSelected,
    optionCount - 1,
  );
  if (minimumSelected <= largestSelectionWithRemainder) {
    visibleCounts.push(optionCount - largestSelectionWithRemainder);
  }
  if (source.required !== true) {
    // Optional multi-select permits a blank answer even when minSelected is
    // positive; unselected mode then exposes the complete option set.
    visibleCounts.push(optionCount);
  }
  return visibleCounts.length > 0 ? Math.min(...visibleCounts) : null;
}

function choiceKindOf(question: JsonObject): string | undefined {
  const response = responseOf(question);
  if (typeof response.kind === "string") return response.kind;
  return typeof question.type === "string" ? question.type : undefined;
}

/**
 * Whether carrying `source`'s options into `target` with `mode` can meet the
 * target's minimum for every valid source answer: the negation of
 * `choices_from_cardinality_unreachable`, from the same two rules. Editors
 * offer only the modes this accepts.
 */
export function isChoicesFromModeReachable(
  source: unknown,
  target: unknown,
  mode: string,
): boolean {
  if (!isRecord(source) || !isRecord(target)) return false;
  const sourceKind = choiceKindOf(source);
  const targetKind = choiceKindOf(target);
  if (sourceKind === undefined || targetKind === undefined) return false;
  const sourceResponse = responseOf(source);
  const minimumVisibleCount = minimumVisibleChoicesFromCount(
    source,
    sourceResponse,
    sourceKind,
    mode,
    optionValuesOf(sourceResponse).size,
  );
  return (
    minimumVisibleCount === null ||
    choicesFromTargetMinimum(target, responseOf(target), targetKind).count <=
      minimumVisibleCount
  );
}

// Additive choice carry-forward. Compiles to SurveyJS choicesFromQuestion.
function validateQuestionChoicesFrom(
  issues: Array<QuestionnaireValidationIssue>,
  researchQuestions: Array<unknown>,
  question: JsonObject,
  index: number,
  questionPath: string,
): void {
  const response = responseOf(question);
  const choicesFrom = response.choicesFrom;
  if (choicesFrom === undefined) {
    return;
  }

  const path = `${questionPath}.response.choicesFrom`;
  // A question with no type reads as "", which no type allows.
  const kind = choiceKindOf(question) ?? "";
  const [targetFailure] = fieldAllowedForType(QUESTION_CAPABILITY_FACTS, {
    questionType: kind,
    capability: "choicesFromTarget",
  });
  if (targetFailure?.reason === "type_not_allowed") {
    addIssue(
      issues,
      path,
      "choices_from_unsupported_target_type",
      repairLine("choices_from_unsupported_target_type", { englishList: String(englishList(targetFailure.types)) }),
    );
    return;
  }

  if (!isRecord(choicesFrom)) {
    addIssue(
      issues,
      path,
      "invalid_choices_from",
      worded("invalid_choices_from", "choicesFrom must be an object."),
    );
    return;
  }

  const mode = choicesFrom.mode;
  if (typeof mode !== "string" || !CHOICES_FROM_MODES.has(mode)) {
    addIssue(
      issues,
      `${path}.mode`,
      "invalid_choices_from",
      worded("invalid_choices_from", "choicesFrom.mode must be 'selected', 'unselected', or 'all'."),
    );
  }

  const sourceStableKey = choicesFrom.sourceStableKey;
  if (typeof sourceStableKey !== "string" || !sourceStableKey) {
    addIssue(
      issues,
      `${path}.sourceStableKey`,
      "invalid_choices_from",
      worded("invalid_choices_from", "choicesFrom.sourceStableKey is required."),
    );
    return;
  }

  const sourceIndex = researchQuestions.findIndex(
    (candidate) =>
      isRecord(candidate) && candidate.stableKey === sourceStableKey,
  );
  if (sourceIndex === -1 || sourceIndex >= index) {
    addIssue(
      issues,
      `${path}.sourceStableKey`,
      "choices_from_invalid_reference",
      repairLine("choices_from_invalid_reference"),
    );
    return;
  }

  const source = researchQuestions[sourceIndex];
  if (!isRecord(source)) {
    return;
  }
  const sourceResponse = responseOf(source);
  const sourceKind = choiceKindOf(source) ?? "";
  const [sourceFailure] = fieldAllowedForType(QUESTION_CAPABILITY_FACTS, {
    questionType: sourceKind,
    capability: "choicesFromSource",
  });
  if (sourceFailure?.reason === "type_not_allowed") {
    addIssue(
      issues,
      `${path}.sourceStableKey`,
      "choices_from_unsupported_source_type",
      repairLine("choices_from_unsupported_source_type", { englishList: String(englishList(sourceFailure.types, "or")) }),
    );
    return;
  }

  const targetValues = optionValuesOf(response);
  const sourceValues = optionValuesOf(sourceResponse);
  const valueSetsMismatch =
    targetValues.size !== sourceValues.size ||
    [...targetValues].some((value) => !sourceValues.has(value));
  if (valueSetsMismatch) {
    addIssue(
      issues,
      path,
      "choices_from_value_set_mismatch",
      repairLine("choices_from_value_set_mismatch"),
    );
  } else {
    const targetMetadata = optionMirrorMetadataOf(response);
    const sourceMetadata = optionMirrorMetadataOf(sourceResponse);
    const metadataMismatch = [...sourceMetadata].some(([value, metadata]) => {
      const target = targetMetadata.get(value);
      return (
        !target ||
        target.label !== metadata.label ||
        target.exclusive !== metadata.exclusive
      );
    });
    if (metadataMismatch) {
      addIssue(
        issues,
        path,
        "choices_from_option_metadata_mismatch",
        repairLine("choices_from_option_metadata_mismatch"),
      );
    }
  }

  if (typeof mode === "string" && CHOICES_FROM_MODES.has(mode)) {
    const targetMinimum = choicesFromTargetMinimum(question, response, kind);
    const minimumVisibleCount = minimumVisibleChoicesFromCount(
      source,
      sourceResponse,
      sourceKind,
      mode,
      sourceValues.size,
    );
    if (
      minimumVisibleCount !== null &&
      targetMinimum.count > minimumVisibleCount
    ) {
      addIssue(
        issues,
        `${questionPath}.response.${targetMinimum.pathSuffix}`,
        "choices_from_cardinality_unreachable",
        repairLine("choices_from_cardinality_unreachable", { mode: String(mode), minimumVisibleCount: String(minimumVisibleCount), count: String(targetMinimum.count) }),
      );
    }
  }
}

// Additive, schema/compiler-level skip-logic / routing capability.
// `visibilityRule` → SurveyJS visibleIf; `routingRule` → SurveyJS triggers.
// Authors never write raw SurveyJS expressions. The registry facts inside
// these rules run through the generic evaluators (`rule-evaluators.ts`), whose
// examples both languages run (questionnaire-rule-evaluator-conformance).
// Skip-logic detection judges its proposals here too, through the applier
// and the write gate (apps/backend/.../drafts/skip-logic-detection.ts).
function validateQuestionVisibilityRule(
  issues: Array<QuestionnaireValidationIssue>,
  researchQuestions: Array<unknown>,
  question: JsonObject,
  index: number,
  questionPath: string,
  assetsById: Map<string, JsonObject>,
): void {
  const rule = question.visibilityRule;
  if (rule === undefined) {
    return;
  }
  validateConditionRuleExpr(
    issues,
    researchQuestions,
    rule,
    index,
    `${questionPath}.visibilityRule`,
    assetsById,
    "visibilityRule",
    false,
  );
}

function validateQuestionRoutingRule(
  issues: Array<QuestionnaireValidationIssue>,
  researchQuestions: Array<unknown>,
  question: JsonObject,
  index: number,
  questionPath: string,
  assetsById: Map<string, JsonObject>,
): void {
  if (question.routingRule === undefined) {
    return;
  }
  validateRoutingRule(
    issues,
    researchQuestions,
    question.routingRule,
    index,
    `${questionPath}.routingRule`,
    assetsById,
  );
}

/**
 * The one TS judge of a condition: every rule a visibility condition, or a
 * route's `when`, must satisfy against the question list it sits in (the
 * source is available at that point; the operator, its fields, value count,
 * threshold, scale, rank and matrix cell fit the source). Each broken rule
 * is its own code. The draft validator, the backend resolver
 * (`runtime.ts`), planBatch and the compiler all read this one function.
 *
 * `hostIndex` is the question the condition sits on; a route's `when`
 * (`allowSelfOrEarlier`) may read that question itself.
 */
export function conditionExprIssues(
  rule: unknown,
  researchQuestions: Array<unknown>,
  options: {
    hostIndex: number;
    allowSelfOrEarlier: boolean;
    assetsById: Map<string, JsonObject>;
    path: string;
  },
): Array<QuestionnaireValidationIssue> {
  const issues: Array<QuestionnaireValidationIssue> = [];
  validateConditionRuleExpr(
    issues,
    researchQuestions,
    rule,
    options.hostIndex,
    options.path,
    options.assetsById,
    options.allowSelfOrEarlier ? "routingRule.when" : "visibilityRule",
    options.allowSelfOrEarlier,
  );
  return issues;
}

/** `conditionExprIssues` for a whole route: its action, target and `when`. */
export function routingRuleIssues(
  rule: unknown,
  researchQuestions: Array<unknown>,
  options: {
    hostIndex: number;
    assetsById: Map<string, JsonObject>;
    path: string;
  },
): Array<QuestionnaireValidationIssue> {
  const issues: Array<QuestionnaireValidationIssue> = [];
  validateRoutingRule(
    issues,
    researchQuestions,
    rule,
    options.hostIndex,
    options.path,
    options.assetsById,
  );
  return issues;
}

function validateRoutingRule(
  issues: Array<QuestionnaireValidationIssue>,
  researchQuestions: Array<unknown>,
  rule: unknown,
  index: number,
  rulePath: string,
  assetsById: Map<string, JsonObject>,
): void {
  if (!isRecord(rule)) {
    addIssue(
      issues,
      rulePath,
      "invalid_routing_rule",
      worded("invalid_routing_rule", "routingRule must be an object."),
    );
    return;
  }

  const action = rule.action;
  if (action !== "skip_to" && action !== "end_survey") {
    addIssue(
      issues,
      `${rulePath}.action`,
      "invalid_routing_rule",
      worded("invalid_routing_rule", "routingRule.action must be 'skip_to' or 'end_survey'."),
    );
    return;
  }

  if ((rule.ruleId != null && (typeof rule.ruleId !== "string" || !rule.ruleId.trim())) ||
      (rule.scope != null && !["respondent", "session"].includes(rule.scope as string)) ||
      (rule.disposition != null && !["branch_complete", "screened_out"].includes(rule.disposition as string)) ||
      (action === "skip_to" && rule.disposition != null)) {
    addIssue(issues, rulePath, "invalid_routing_rule", worded("invalid_routing_rule", "Invalid route identity, scope or terminal disposition."));
  }

  const targetStableKey = rule.targetStableKey;
  if (action === "skip_to") {
    if (typeof targetStableKey !== "string" || !targetStableKey) {
      addIssue(
        issues,
        `${rulePath}.targetStableKey`,
        "invalid_routing_rule",
        worded("invalid_routing_rule", "routingRule.targetStableKey is required when action is 'skip_to'."),
      );
    } else {
      const targetIndex = researchQuestions.findIndex(
        (candidate) =>
          isRecord(candidate) && candidate.stableKey === targetStableKey,
      );
      if (targetIndex === -1 || targetIndex <= index) {
        addIssue(
          issues,
          `${rulePath}.targetStableKey`,
          "routing_rule_invalid_target",
          repairLine("routing_rule_invalid_target"),
        );
      } else if (targetIndex === index + 1) {
        // A skip lands on its target. The next research question is the next
        // page, so this route skips nothing.
        addIssue(
          issues,
          `${rulePath}.targetStableKey`,
          "routing_rule_noop_target",
          repairLine("routing_rule_noop_target"),
        );
      }
    }
  } else if (targetStableKey !== undefined && targetStableKey !== null) {
    addIssue(
      issues,
      `${rulePath}.targetStableKey`,
      "invalid_routing_rule",
      worded("invalid_routing_rule", "routingRule.targetStableKey must be omitted when action is 'end_survey'."),
    );
  }

  if (rule.when === undefined) {
    addIssue(
      issues,
      `${rulePath}.when`,
      "invalid_routing_rule",
      worded("invalid_routing_rule", "routingRule.when is required."),
    );
    return;
  }

  validateConditionRuleExpr(
    issues,
    researchQuestions,
    rule.when,
    index,
    `${rulePath}.when`,
    assetsById,
    "routingRule.when",
    true,
  );
}

const CONDITION_FACTS = { operators: CONDITION_OPERATOR_FAMILIES };

/** "rating or matrix_rating": the question types a family's operators read. */
function sourceTypesText(family: ConditionOperatorFamily): string {
  return englishList([...CONDITION_OPERATOR_FAMILIES[family].sourceTypes], "or");
}

/** "'equals'/'not_equals'": a family's operators that name exactly one value. */
function oneValueOperatorsText(family: ConditionOperatorFamily): string {
  const { values, operators } = CONDITION_OPERATOR_FAMILIES[family];
  return Object.keys(operators)
    .filter((operator) => (operators[operator].values ?? values) === "one")
    .map((operator) => `'${operator}'`)
    .join("/");
}

function validateConditionRuleExpr(
  issues: Array<QuestionnaireValidationIssue>,
  researchQuestions: Array<unknown>,
  rule: unknown,
  index: number,
  rulePath: string,
  assetsById: Map<string, JsonObject>,
  fieldLabel: string,
  allowSelfOrEarlier: boolean,
): void {
  if (!isRecord(rule)) {
    addIssue(
      issues,
      rulePath,
      "visibility_rule_malformed",
      worded("visibility_rule_malformed", `${fieldLabel} must be an object.`),
    );
    return;
  }

  if (isVisibilityRuleGroupRecord(rule)) {
    if (rule.join !== "and" && rule.join !== "or") {
      addIssue(
        issues,
        `${rulePath}.join`,
        "visibility_rule_malformed",
        worded("visibility_rule_malformed", `${fieldLabel}.join must be 'and' or 'or'.`),
      );
      return;
    }
    const rules = asArray(rule.rules);
    if (rules.length === 0) {
      addIssue(
        issues,
        `${rulePath}.rules`,
        "visibility_rule_group_size",
        worded("visibility_rule_group_size", `${fieldLabel}.rules must be a non-empty array of leaf rules.`),
      );
      return;
    }
    const [tooMany] = countWithinBounds(
      { max: MAX_VISIBILITY_RULE_GROUP_SIZE },
      { count: rules.length },
    );
    if (tooMany?.reason === "above_max") {
      addIssue(
        issues,
        `${rulePath}.rules`,
        "visibility_rule_group_size",
        worded("visibility_rule_group_size", `${fieldLabel}.rules must contain at most ${tooMany.max} leaf rules.`),
      );
      return;
    }
    rules.forEach((leaf, leafIndex) => {
      const leafPath = `${rulePath}.rules[${leafIndex}]`;
      if (!isRecord(leaf)) {
        addIssue(
          issues,
          leafPath,
          "visibility_rule_malformed",
          worded("visibility_rule_malformed", `${fieldLabel}.rules entries must be leaf visibility rules.`),
        );
        return;
      }
      if (isVisibilityRuleGroupRecord(leaf)) {
        addIssue(
          issues,
          leafPath,
          "visibility_rule_malformed",
          worded("visibility_rule_malformed", `${fieldLabel}.rules must not contain nested groups.`),
        );
        return;
      }
      validateQuestionVisibilityRuleLeaf(
        issues,
        researchQuestions,
        leaf,
        index,
        leafPath,
        assetsById,
        fieldLabel,
        allowSelfOrEarlier,
      );
    });
    return;
  }

  validateQuestionVisibilityRuleLeaf(
    issues,
    researchQuestions,
    rule,
    index,
    rulePath,
    assetsById,
    fieldLabel,
    allowSelfOrEarlier,
  );
}

function validateQuestionVisibilityRuleLeaf(
  issues: Array<QuestionnaireValidationIssue>,
  researchQuestions: Array<unknown>,
  rule: JsonObject,
  index: number,
  rulePath: string,
  assetsById: Map<string, JsonObject>,
  fieldLabel: string,
  allowSelfOrEarlier: boolean,
): void {
  const dependsOnStableKey = rule.dependsOnStableKey;
  const operator = rule.operator;
  const values = asArray(rule.values);
  const threshold = rule.threshold;
  const thresholdHigh = rule.thresholdHigh;
  const matrixRowValue = rule.matrixRowValue;
  const matrixMeasureId = rule.matrixMeasureId;
  const rankK = rule.rankK;
  const hasMatrixRowValue =
    matrixRowValue !== undefined && matrixRowValue !== null;
  const hasMatrixMeasureId =
    matrixMeasureId !== undefined && matrixMeasureId !== null;
  const hasRankK = rankK !== undefined && rankK !== null;
  const hasValues = rule.values !== undefined && rule.values !== null;
  const hasThresholdHigh =
    thresholdHigh !== undefined && thresholdHigh !== null;
  const operatorFamily = conditionOperatorFamily(operator);
  const isNumericOperator = operatorFamily === "numeric";
  const isChoiceOperator = operatorFamily === "choice";
  const isRankingOperator = operatorFamily === "ranking";
  const isNotApplicableOperator = operatorFamily === "not_applicable";

  if (typeof dependsOnStableKey !== "string" || !dependsOnStableKey) {
    addIssue(
      issues,
      `${rulePath}.dependsOnStableKey`,
      "visibility_rule_malformed",
      worded("visibility_rule_malformed", `${fieldLabel}.dependsOnStableKey must be a non-empty string.`),
    );
    return;
  }

  const [unknownOperator] = operatorReadsSource(CONDITION_FACTS, { operator });
  if (unknownOperator?.reason === "unknown_operator") {
    addIssue(
      issues,
      `${rulePath}.operator`,
      "visibility_rule_unknown_operator",
      repairLine("visibility_rule_unknown_operator", { fieldLabel: String(fieldLabel), operatorsValue: String(unknownOperator.operators.join(", ")) }),
    );
    return;
  }

  // Visibility: strictly earlier. Routing when: host question or earlier.
  const dependencyIndex = researchQuestions.findIndex(
    (candidate) =>
      isRecord(candidate) && candidate.stableKey === dependsOnStableKey,
  );

  const dependencyOutOfRange = allowSelfOrEarlier
    ? dependencyIndex === -1 || dependencyIndex > index
    : dependencyIndex === -1 || dependencyIndex >= index;

  if (dependencyOutOfRange) {
    addIssue(
      issues,
      `${rulePath}.dependsOnStableKey`,
      "visibility_rule_invalid_reference",
      worded("visibility_rule_invalid_reference", allowSelfOrEarlier
        ? `${fieldLabel}.dependsOnStableKey must reference this question or an earlier question in researchQuestions.`
        : `${fieldLabel}.dependsOnStableKey must reference an earlier question in researchQuestions.`),
    );
    return;
  }

  const dependency = researchQuestions[dependencyIndex];
  if (!isRecord(dependency)) {
    return;
  }
  // The registry facts of this leaf: whether its operator reads the
  // dependency's type, and names as many values as its family says.
  const operatorFailures = operatorReadsSource(
    CONDITION_FACTS,
    isNotApplicableOperator
      ? { operator, sourceType: dependency.type }
      : { operator, sourceType: dependency.type, valueCount: values.length },
  );
  const valueCountFailure = operatorFailures.find(
    (failure) => failure.reason === "value_count",
  );
  const familySourceFailure = operatorFailures.find(
    (failure) => failure.reason === "family_source_type",
  );
  const operatorSourceFailure = operatorFailures.find(
    (failure) => failure.reason === "operator_source_type",
  );

  if (isNotApplicableOperator) {
    if (hasValues) {
      addIssue(issues, `${rulePath}.values`, "visibility_rule_field_not_for_operator", worded("visibility_rule_field_not_for_operator", `${fieldLabel}.values must be omitted for is_not_applicable.`));
    }
    if (threshold !== undefined && threshold !== null) {
      addIssue(issues, `${rulePath}.threshold`, "visibility_rule_field_not_for_operator", worded("visibility_rule_field_not_for_operator", `${fieldLabel}.threshold must be omitted for is_not_applicable.`));
    }
    if (hasThresholdHigh) {
      addIssue(issues, `${rulePath}.thresholdHigh`, "visibility_rule_field_not_for_operator", worded("visibility_rule_field_not_for_operator", `${fieldLabel}.thresholdHigh must be omitted for is_not_applicable.`));
    }
    if (hasRankK) {
      addIssue(issues, `${rulePath}.rankK`, "visibility_rule_field_not_for_operator", worded("visibility_rule_field_not_for_operator", `${fieldLabel}.rankK must be omitted for is_not_applicable.`));
    }
    const response = responseOf(dependency);
    if (dependency.type === "matrix_rating") {
      validateMatrixRatingVisibilityRule(
        issues, rulePath, fieldLabel, response, null, matrixRowValue,
        matrixMeasureId, hasMatrixRowValue, hasMatrixMeasureId, assetsById,
        undefined, true,
      );
      return;
    }
    if (hasMatrixRowValue || hasMatrixMeasureId) {
      addIssue(issues, `${rulePath}.${hasMatrixRowValue ? "matrixRowValue" : "matrixMeasureId"}`, "visibility_rule_field_not_for_operator", worded("visibility_rule_field_not_for_operator", `${fieldLabel}.matrixRowValue/matrixMeasureId are only valid for matrix_rating dependencies.`));
    }
    if (familySourceFailure || response.includeNotApplicable !== true) {
      addIssue(issues, `${rulePath}.dependsOnStableKey`, "visibility_rule_unsupported_dependency_type", worded("visibility_rule_unsupported_dependency_type", `${fieldLabel} is_not_applicable can only depend on a ${sourceTypesText("not_applicable")} measure with includeNotApplicable.`));
    }
    return;
  }

  if (isNumericOperator) {
    if (hasRankK) {
      addIssue(
        issues,
        `${rulePath}.rankK`,
        "visibility_rule_field_not_for_operator",
        worded("visibility_rule_field_not_for_operator", `${fieldLabel}.rankK must be omitted for numeric operators.`),
      );
    }
    if (valueCountFailure) {
      addIssue(
        issues,
        `${rulePath}.values`,
        "visibility_rule_field_not_for_operator",
        worded("visibility_rule_field_not_for_operator", `${fieldLabel}.values must be omitted for numeric operators (${NUMERIC_CONDITION_OPERATORS.join("/")}).`),
      );
    }
    if (typeof threshold !== "number" || !Number.isFinite(threshold)) {
      addIssue(
        issues,
        `${rulePath}.threshold`,
        "visibility_rule_threshold_invalid",
        worded("visibility_rule_threshold_invalid", `${fieldLabel}.threshold must be a finite number for numeric operators (${NUMERIC_CONDITION_OPERATORS.join("/")}).`),
      );
      return;
    }
    if (operator === "between") {
      if (
        typeof thresholdHigh !== "number" ||
        !Number.isFinite(thresholdHigh)
      ) {
        addIssue(
          issues,
          `${rulePath}.thresholdHigh`,
          "visibility_rule_threshold_invalid",
          worded("visibility_rule_threshold_invalid", `${fieldLabel}.thresholdHigh must be a finite number for 'between'.`),
        );
        return;
      }
      if (threshold > thresholdHigh) {
        addIssue(
          issues,
          `${rulePath}.thresholdHigh`,
          "visibility_rule_threshold_invalid",
          worded("visibility_rule_threshold_invalid", `${fieldLabel}.threshold must be less than or equal to thresholdHigh for 'between'.`),
        );
      }
    } else if (hasThresholdHigh) {
      addIssue(
        issues,
        `${rulePath}.thresholdHigh`,
        "visibility_rule_field_not_for_operator",
        worded("visibility_rule_field_not_for_operator", `${fieldLabel}.thresholdHigh must be omitted for non-between operators.`),
      );
    }
    if (familySourceFailure?.reason === "family_source_type") {
      addIssue(
        issues,
        `${rulePath}.dependsOnStableKey`,
        "visibility_rule_unsupported_dependency_type",
        worded("visibility_rule_unsupported_dependency_type", `${fieldLabel} numeric operators can only depend on ${englishList(familySourceFailure.sourceTypes, "or")} questions.`),
      );
      return;
    }
    const response = responseOf(dependency);
    const betweenHigh =
      operator === "between" &&
      typeof thresholdHigh === "number" &&
      Number.isFinite(thresholdHigh)
        ? thresholdHigh
        : undefined;
    if (dependency.type === "matrix_rating") {
      validateMatrixRatingVisibilityRule(
        issues,
        rulePath,
        fieldLabel,
        response,
        threshold,
        matrixRowValue,
        matrixMeasureId,
        hasMatrixRowValue,
        hasMatrixMeasureId,
        assetsById,
        betweenHigh,
      );
      return;
    }
    if (hasMatrixRowValue || hasMatrixMeasureId) {
      addIssue(
        issues,
        `${rulePath}.${hasMatrixRowValue ? "matrixRowValue" : "matrixMeasureId"}`,
        "visibility_rule_field_not_for_operator",
        worded("visibility_rule_field_not_for_operator", `${fieldLabel}.matrixRowValue/matrixMeasureId are only valid for matrix_rating dependencies.`),
      );
    }
    if (dependency.type === "rating") {
      const unit = response.unit;
      if (typeof unit !== "string" || !isRatingUnitId(unit)) {
        addIssue(
          issues,
          `${rulePath}.dependsOnStableKey`,
          "visibility_rule_source_incomplete",
          worded("visibility_rule_source_incomplete", `${fieldLabel} rating dependency must have a valid rating unit.`),
        );
        return;
      }
      const [min, max] = ratingBounds(unit);
      if (threshold < min || threshold > max) {
        addIssue(
          issues,
          `${rulePath}.threshold`,
          "visibility_rule_threshold_out_of_bounds",
          worded("visibility_rule_threshold_out_of_bounds", `${fieldLabel}.threshold must be between ${min} and ${max} for unit ${unit}.`),
        );
      }
      if (
        typeof betweenHigh === "number" &&
        (betweenHigh < min || betweenHigh > max)
      ) {
        addIssue(
          issues,
          `${rulePath}.thresholdHigh`,
          "visibility_rule_threshold_out_of_bounds",
          worded("visibility_rule_threshold_out_of_bounds", `${fieldLabel}.thresholdHigh must be between ${min} and ${max} for unit ${unit}.`),
        );
      }
      return;
    }

    const unit = response.unit;
    if (typeof unit !== "string" || !isContinuousUnitId(unit)) {
      addIssue(
        issues,
        `${rulePath}.dependsOnStableKey`,
        "visibility_rule_source_incomplete",
        worded("visibility_rule_source_incomplete", `${fieldLabel} continuous dependency must have a valid continuous unit.`),
      );
      return;
    }
    const bounds = continuousThresholdBounds(response);
    if (bounds) {
      const [min, max] = bounds;
      const rangeLabel =
        Number.isFinite(min) && Number.isFinite(max)
          ? `between ${min} and ${max}`
          : Number.isFinite(min)
            ? `>= ${min}`
            : `<= ${max}`;
      if (threshold < min || threshold > max) {
        addIssue(
          issues,
          `${rulePath}.threshold`,
          "visibility_rule_threshold_out_of_bounds",
          worded("visibility_rule_threshold_out_of_bounds", `${fieldLabel}.threshold must be ${rangeLabel} for this continuous dependency.`),
        );
      }
      if (
        typeof betweenHigh === "number" &&
        (betweenHigh < min || betweenHigh > max)
      ) {
        addIssue(
          issues,
          `${rulePath}.thresholdHigh`,
          "visibility_rule_threshold_out_of_bounds",
          worded("visibility_rule_threshold_out_of_bounds", `${fieldLabel}.thresholdHigh must be ${rangeLabel} for this continuous dependency.`),
        );
      }
    }
    return;
  }

  if (isRankingOperator) {
    if (hasMatrixRowValue || hasMatrixMeasureId) {
      addIssue(
        issues,
        `${rulePath}.${hasMatrixRowValue ? "matrixRowValue" : "matrixMeasureId"}`,
        "visibility_rule_field_not_for_operator",
        worded("visibility_rule_field_not_for_operator", `${fieldLabel}.matrixRowValue/matrixMeasureId must be omitted for ranking operators.`),
      );
    }
    if (threshold !== undefined && threshold !== null) {
      addIssue(
        issues,
        `${rulePath}.threshold`,
        "visibility_rule_field_not_for_operator",
        worded("visibility_rule_field_not_for_operator", `${fieldLabel}.threshold must be omitted for ranking operators.`),
      );
    }
    if (hasThresholdHigh) {
      addIssue(
        issues,
        `${rulePath}.thresholdHigh`,
        "visibility_rule_field_not_for_operator",
        worded("visibility_rule_field_not_for_operator", `${fieldLabel}.thresholdHigh must be omitted for ranking operators.`),
      );
    }
    if (typeof rankK !== "number" || !Number.isInteger(rankK) || rankK < 1) {
      addIssue(
        issues,
        `${rulePath}.rankK`,
        "visibility_rule_rank_k_out_of_bounds",
        worded("visibility_rule_rank_k_out_of_bounds", `${fieldLabel}.rankK must be a positive integer for ranking operators.`),
      );
      return;
    }
    if (
      values.length === 0 ||
      !values.every((value) => typeof value === "string" && value.length > 0)
    ) {
      addIssue(
        issues,
        `${rulePath}.values`,
        "visibility_rule_values_missing",
        repairLine("visibility_rule_values_missing", { fieldLabel: String(fieldLabel) }),
      );
      return;
    }
    if (valueCountFailure) {
      addIssue(
        issues,
        `${rulePath}.values`,
        "visibility_rule_value_count",
        repairLine("visibility_rule_value_count", { fieldLabel: String(fieldLabel), oneValueOperatorsText: String(oneValueOperatorsText("ranking")) }),
      );
    }
    if (familySourceFailure?.reason === "family_source_type") {
      addIssue(
        issues,
        `${rulePath}.dependsOnStableKey`,
        "visibility_rule_unsupported_dependency_type",
        worded("visibility_rule_unsupported_dependency_type", `${fieldLabel} ranking operators can only depend on ${englishList(familySourceFailure.sourceTypes, "or")} questions.`),
      );
      return;
    }
    const response = responseOf(dependency);
    const assumedLength = rankingMaxLength(response);
    if (assumedLength < 1) {
      addIssue(
        issues,
        `${rulePath}.dependsOnStableKey`,
        "visibility_rule_source_incomplete",
        worded("visibility_rule_source_incomplete", `${fieldLabel} ranking dependency must have at least one option.`),
      );
      return;
    }
    if (rankK > assumedLength) {
      addIssue(
        issues,
        `${rulePath}.rankK`,
        "visibility_rule_rank_k_out_of_bounds",
        worded("visibility_rule_rank_k_out_of_bounds", `${fieldLabel}.rankK must be between 1 and ${assumedLength} for this ranking dependency.`),
      );
    }
    if (operator === "rank_in_bottom" && rankingFixedLength(response) === null) {
      addIssue(
        issues,
        `${rulePath}.operator`,
        "visibility_rule_rank_bottom_variable_length",
        repairLine("visibility_rule_rank_bottom_variable_length", { fieldLabel: String(fieldLabel) }),
      );
    }
    const optionValues = new Set(
      asArray(response.options)
        .filter(isRecord)
        .map((option) => option.value)
        .filter((value): value is string => typeof value === "string"),
    );
    const unknownValues = values.filter(
      (value) =>
        typeof value !== "string" ||
        !optionValues.has(value),
    );
    if (unknownValues.length > 0) {
      addIssue(
        issues,
        `${rulePath}.values`,
        "visibility_rule_unknown_value",
        repairLine("visibility_rule_unknown_value", { fieldLabel: String(fieldLabel), unknownValues: String(unknownValues.join(", ")) }),
      );
    }
    return;
  }

  if (isChoiceOperator) {
    const matrixCategorical = dependency.type === "matrix_categorical";
    if (
      (!matrixCategorical && (hasMatrixRowValue || hasMatrixMeasureId)) ||
      (matrixCategorical && hasMatrixMeasureId)
    ) {
      addIssue(
        issues,
        `${rulePath}.${hasMatrixMeasureId ? "matrixMeasureId" : "matrixRowValue"}`,
        "visibility_rule_field_not_for_operator",
        worded("visibility_rule_field_not_for_operator", matrixCategorical
          ? `${fieldLabel}.matrixMeasureId must be omitted for matrix_categorical dependencies.`
          : `${fieldLabel}.matrixRowValue/matrixMeasureId must be omitted for choice operators.`),
      );
    }
    if (hasRankK) {
      addIssue(
        issues,
        `${rulePath}.rankK`,
        "visibility_rule_field_not_for_operator",
        worded("visibility_rule_field_not_for_operator", `${fieldLabel}.rankK must be omitted for choice operators.`),
      );
    }
    if (threshold !== undefined && threshold !== null) {
      addIssue(
        issues,
        `${rulePath}.threshold`,
        "visibility_rule_field_not_for_operator",
        worded("visibility_rule_field_not_for_operator", `${fieldLabel}.threshold must be omitted for choice operators.`),
      );
    }
    if (hasThresholdHigh) {
      addIssue(
        issues,
        `${rulePath}.thresholdHigh`,
        "visibility_rule_field_not_for_operator",
        worded("visibility_rule_field_not_for_operator", `${fieldLabel}.thresholdHigh must be omitted for choice operators.`),
      );
    }
    if (
      values.length === 0 ||
      !values.every((value) => typeof value === "string" && value.length > 0)
    ) {
      addIssue(
        issues,
        `${rulePath}.values`,
        "visibility_rule_values_missing",
        repairLine("visibility_rule_values_missing", { fieldLabel: String(fieldLabel) }),
      );
      return;
    }

    if (valueCountFailure) {
      addIssue(
        issues,
        `${rulePath}.values`,
        "visibility_rule_value_count",
        repairLine("visibility_rule_value_count", { fieldLabel: String(fieldLabel), oneValueOperatorsText: String(oneValueOperatorsText("choice")) }),
      );
    }

    if (familySourceFailure?.reason === "family_source_type") {
      addIssue(
        issues,
        `${rulePath}.dependsOnStableKey`,
        "visibility_rule_unsupported_dependency_type",
        worded("visibility_rule_unsupported_dependency_type", `${fieldLabel} choice operators can only depend on ${englishList(familySourceFailure.sourceTypes, "or")} questions.`),
      );
      return;
    }

    // equals/not_equals compile to scalar equality (!=); on multi_select the
    // compiler would otherwise silently emit anyof/notcontains (membership).
    // An operator that reads one answer names the source types it reads, and
    // its set operator (any_of/none_of) reads a multi select's answer set.
    if (operatorSourceFailure?.reason === "operator_source_type") {
      addIssue(
        issues,
        `${rulePath}.operator`,
        "visibility_rule_unsupported_dependency_type",
        worded("visibility_rule_unsupported_dependency_type", `${fieldLabel} '${operatorSourceFailure.operator}' reads only a ${englishList(operatorSourceFailure.sourceTypes, "or")} dependency; use ${conditionMembershipOperator(operatorSourceFailure.operator)} for ${String(dependency.type)}.`),
      );
      return;
    }

    if (matrixCategorical) {
      validateMatrixCategoricalVisibilityRule(
        issues,
        rulePath,
        fieldLabel,
        responseOf(dependency),
        matrixRowValue,
        hasMatrixRowValue,
        assetsById,
      );
    }
    const optionValues = new Set(
      asArray(
        matrixCategorical
          ? responseOf(dependency).categories
          : responseOf(dependency).options,
      )
        .filter(isRecord)
        .map((option) => option.value)
        .filter((value): value is string => typeof value === "string"),
    );

    const unknownValues = values.filter(
      (value) =>
        typeof value !== "string" ||
        !optionValues.has(value),
    );
    if (unknownValues.length > 0) {
      addIssue(
        issues,
        `${rulePath}.values`,
        "visibility_rule_unknown_value",
        repairLine("visibility_rule_unknown_value", { fieldLabel: String(fieldLabel), unknownValues: String(unknownValues.join(", ")) }),
      );
    }
  }
}

/** A template token: `{{` and `}}` around one identifier. */
const TEMPLATE_PLACEHOLDER = /\{\{\s*[A-Za-z0-9_]+\s*\}\}/;

/**
 * Each text a respondent reads in one question, with its path: its title,
 * description and every string of its response (options, rows, measures,
 * anchors, point labels). Stable IDs, values, references and routing
 * metadata are identities, not text respondents read.
 */
function forEachRespondentText(
  question: JsonObject,
  questionPath: string,
  visit: (value: unknown, path: string) => void,
): void {
  const checkFields = (
    record: JsonObject,
    path: string,
    fields: string[],
  ): void => {
    fields.forEach((field) => visit(record[field], `${path}.${field}`));
  };
  const checkItems = (items: unknown, path: string): void => {
    asArray(items).forEach((item, index) => {
      if (isRecord(item))
        checkFields(item, `${path}[${index}]`, ["label", "description"]);
    });
  };
  const checkRatingCopy = (rating: JsonObject, path: string): void => {
    if (isRecord(rating.anchors)) {
      checkFields(rating.anchors, `${path}.anchors`, [
        "minLabel",
        "maxLabel",
        // A midpoint reaches respondents only where the unit has one.
        ...(ratingHasMidpoint(rating.unit) ? ["midpointLabel"] : []),
      ]);
    }
    asArray(rating.pointLabels).forEach((label, index) =>
      visit(label, `${path}.pointLabels[${index}]`),
    );
    visit(rating.notApplicableLabel, `${path}.notApplicableLabel`);
  };
  checkFields(question, questionPath, ["title", "description"]);
  if (!isRecord(question.response)) return;
  const response = question.response;
  const responsePath = `${questionPath}.response`;
  switch (response.kind) {
    case "single_select":
    case "multi_select":
    case "ranking":
      checkItems(response.options, `${responsePath}.options`);
      break;
    case "rating":
      checkRatingCopy(response, responsePath);
      break;
    case "matrix_rating":
    case "matrix_categorical":
      if (isRecord(response.rows) && response.rows.source === "fixed") {
        checkItems(response.rows.rows, `${responsePath}.rows.rows`);
      }
      if (response.kind === "matrix_categorical") {
        checkItems(response.categories, `${responsePath}.categories`);
      } else {
        asArray(response.measures).forEach((measure, index) => {
          if (!isRecord(measure)) return;
          const path = `${responsePath}.measures[${index}]`;
          visit(measure.label, `${path}.label`);
          checkRatingCopy(measure, path);
        });
      }
      break;
    case "open_end":
      checkFields(response, responsePath, ["placeholder"]);
      break;
  }
}

/**
 * Text a respondent reads never carries an unfilled template token such as
 * `{{brand}}`: twins would read the braces. A literal token check on the
 * draft, so Review, Save and the chat repair refuse it with its code before
 * compile does.
 */
function validateTemplatePlaceholders(
  issues: Array<QuestionnaireValidationIssue>,
  question: JsonObject,
  questionPath: string,
): void {
  forEachRespondentText(question, questionPath, (value, path) => {
    if (typeof value !== "string") return;
    const placeholder = value.match(TEMPLATE_PLACEHOLDER)?.[0];
    if (placeholder) {
      addIssue(
        issues,
        path,
        "respondent_text_template_placeholder",
        repairLine("respondent_text_template_placeholder", {
          placeholder: String(placeholder),
        }),
      );
    }
  });
}

/**
 * A stored question's text names each tested asset by its token, and every
 * token names an asset of the catalog (`assetMentionIssuesIn`, by the token
 * grammar alone). Read on the stored text, before tokens are rendered: a
 * model writes the token it was shown, and the Save binds the builder's
 * typed `@Name` before the draft is validated, so an `@` mention left in
 * stored text is one nothing bound, which respondents would read literally.
 */
function validateAssetMentions(
  issues: Array<QuestionnaireValidationIssue>,
  stored: unknown,
  questionPath: string,
  catalogKeys: ReadonlySet<string>,
): void {
  if (!isRecord(stored)) return;
  forEachRespondentText(stored, questionPath, (value, path) => {
    assetMentionIssuesIn(value, catalogKeys).forEach((issue) =>
      addIssue(
        issues,
        path,
        issue.code,
        issue.code === "asset_mention_key_unknown"
          ? repairLine(issue.code, { token: issue.token })
          : repairLine(issue.code),
      ),
    );
  });
}

function validateDisallowedLogic(
  issues: Array<QuestionnaireValidationIssue>,
  value: unknown,
  path: string,
): void {
  if (!isRecord(value) && !Array.isArray(value)) {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) =>
      validateDisallowedLogic(issues, item, `${path}[${index}]`),
    );
    return;
  }

  // Tested assets are opaque stimulus data, not authored SurveyJS config:
  // their metadata may legitimately carry keys such as `expression`.
  if (path === "testedAssets") {
    return;
  }

  [
    "visibleIf",
    "enableIf",
    "triggers",
    "calculatedValues",
    "branching",
    "piping",
    "expression",
  ].forEach((key) => {
    if (key in value) {
      addIssue(
        issues,
        path ? `${path}.${key}` : key,
        "advanced_logic_disallowed",
        repairLine("advanced_logic_disallowed"),
      );
    }
  });

  Object.entries(value).forEach(([key, childValue]) => {
    validateDisallowedLogic(issues, childValue, path ? `${path}.${key}` : key);
  });
}

/** The held-back fact an unaided brand read is asked without (wave D, D2). */
const BRAND_FACT = "brand";

/**
 * A question that tells the brand an unaided brand read must be asked
 * without: a list or grid derived from the brand roster, or a lift pre half.
 */
function showsBrand(question: Record<string, unknown>): boolean {
  const response = isRecord(question.response) ? question.response : null;
  return (
    (isRecord(response?.optionSource) && response.optionSource.kind === "brand_roster") ||
    (isRecord(response?.rows) && response.rows.derivedFrom === "brand_roster") ||
    asArray(question.tags).some((tag) => readLiftTag(tag)?.half === "pre")
  );
}

/**
 * Held-back facts in the stored questionnaire (wave D, D2): a question asked
 * without a fact comes before the first question that tells it. The facts are
 * typed: PLAN's (`planAttribution.answeredWithout` / `reveals`), and the brand,
 * which a question of a recipe module that reads it unprompted
 * (`brandUndisclosedModuleIds`) is asked without and a question that shows it
 * (`showsBrand`) tells. Re-run on every write, so a user's drag, a deleted
 * reveal or a chat insert is said; the catalog entry's writer split refuses a
 * model batch and shows a user's edit.
 */
export function heldBackReadIssues(
  questions: ReadonlyArray<unknown>,
  brandUndisclosedModuleIds: ReadonlyArray<string> = [],
): Array<QuestionnaireValidationIssue> {
  const brandReads = new Set(brandUndisclosedModuleIds);
  const keys = (question: unknown, field: "answeredWithout" | "reveals"): Array<string> => {
    if (!isRecord(question)) return [];
    const attribution = isRecord(question.planAttribution) ? question.planAttribution : {};
    const typed = asArray(attribution[field]).filter((key): key is string => typeof key === "string");
    const brand =
      field === "reveals"
        ? showsBrand(question)
        : typeof attribution.recipeModuleId === "string" && brandReads.has(attribution.recipeModuleId);
    return brand ? [...typed, BRAND_FACT] : typed;
  };
  const revealedAt = new Map<string, number>();
  questions.forEach((question, index) => {
    for (const key of keys(question, "reveals")) {
      if (!revealedAt.has(key)) revealedAt.set(key, index);
    }
  });
  const issues: Array<QuestionnaireValidationIssue> = [];
  const severity = policyFor("held_back_read_after_reveal", "user") === "warn" ? "warning" : "error";
  questions.forEach((question, index) => {
    for (const key of keys(question, "answeredWithout")) {
      const revealIndex = revealedAt.get(key);
      // The brand need not be told at all; any other fact is told later.
      if (revealIndex === undefined ? key === BRAND_FACT : revealIndex > index) continue;
      const params = {
        questionKey: isRecord(question) ? String(question.stableKey ?? index) : String(index),
        factKey: key,
      };
      addIssue(
        issues,
        `researchQuestions[${index}].planAttribution.answeredWithout`,
        "held_back_read_after_reveal",
        repairLine("held_back_read_after_reveal", params),
        severity,
      );
    }
  });
  return issues;
}

export function validateQuestionnaireDraftPayload(
  stored: unknown,
): DraftValidationResult {
  const issues: Array<QuestionnaireValidationIssue> = [];
  // The text rules read the text respondents and the deck are given: each
  // stored asset token as its asset's `@Name` (D2 S3.1). Identity is read
  // from typed refs, never from the text. Only the mention rule reads the
  // stored tokens (`validateAssetMentions`).
  const payload = isRecord(stored)
    ? renderAssetRefTokens(stored, stored.testedAssets)
    : stored;
  if (!isRecord(payload)) {
    addIssue(
      issues,
      "",
      "draft_payload_invalid",
      worded("draft_payload_invalid", "Questionnaire draft payload must be an object."),
    );
    return { status: "invalid", issues };
  }

  const seenStableKeys = new Map<string, string>();

  const researchQuestions = asArray(payload.researchQuestions);
  const [questionCount] = countWithinBounds(
    {
      min: resolveResearchQuestionFloor(payload),
      max: RESEARCH_QUESTION_CEILING,
    },
    { count: researchQuestions.length },
  );
  if (questionCount?.reason === "below_min") {
    addIssue(
      issues,
      "researchQuestions",
      "research_questions_too_few",
      repairLine("research_questions_too_few", { minValue: String(questionCount.min) }),
    );
  }
  if (questionCount?.reason === "above_max") {
    addIssue(
      issues,
      "researchQuestions",
      "research_questions_too_many",
      repairLine("research_questions_too_many", { max: String(questionCount.max) }),
    );
  }
  if (!Array.isArray(payload.researchQuestions)) {
    addIssue(
      issues,
      "researchQuestions",
      "draft_payload_invalid",
      worded("draft_payload_invalid", "Questionnaire draft researchQuestions must be an array."),
    );
  }

  const testedAssets = isRecord(payload.testedAssets)
    ? payload.testedAssets
    : {};
  const { assetTypes: declaredAssetTypes, assetsById } = validateTestedAssets(
    issues,
    testedAssets,
    seenStableKeys,
  );
  const matrixAssetContext: MatrixAssetContext = {
    declaredAssetTypes,
    testedAssets: asArray(testedAssets.assets).filter(isRecord),
  };
  // Option labels compare as respondents read them, in the study's language.
  const wording = respondentWordingFor(
    typeof payload.contentLanguage === "string" ? payload.contentLanguage : undefined,
  );

  const compositeMetricIds = collectCompositeMetricIds(payload);
  const storedQuestions = asArray(isRecord(stored) ? stored.researchQuestions : []);
  const catalogKeys = new Set(
    asArray(testedAssets.assets).flatMap((asset) =>
      isRecord(asset) && hasText(asset.stableKey) ? [asset.stableKey] : [],
    ),
  );
  researchQuestions.forEach((question, index) => {
    if (!isRecord(question)) {
      addIssue(
        issues,
        `researchQuestions[${index}]`,
        "draft_payload_invalid",
        worded("draft_payload_invalid", "Every research question must be an object."),
      );
      return;
    }

    const questionPath = `researchQuestions[${index}]`;
    collectStableKey(
      issues,
      seenStableKeys,
      question.stableKey,
      `${questionPath}.stableKey`,
    );
    validateQuestion(
      issues,
      question,
      questionPath,
      wording,
      matrixAssetContext,
      assetsById,
    );
    validateTemplatePlaceholders(issues, question, questionPath);
    validateAssetMentions(issues, storedQuestions[index], questionPath, catalogKeys);
    validateRecipeModuleQuestionType(issues, question, questionPath, payload);
    validateAssetBindings(issues, question, questionPath, declaredAssetTypes);
    validateAssetRefs(issues, question, questionPath, assetsById);
    validateChoiceOptionAssetRefs(
      issues,
      isRecord(question.response) ? question.response : {},
      `${questionPath}.response`,
      assetsById,
    );
    validateCompositeBounds(issues, question, questionPath, compositeMetricIds);
    validateOpenEndCoverage(issues, payload, question, questionPath);
    validateQuestionVisibilityRule(
      issues,
      researchQuestions,
      question,
      index,
      questionPath,
      assetsById,
    );
    validateQuestionRoutingRule(
      issues,
      researchQuestions,
      question,
      index,
      questionPath,
      assetsById,
    );
    validateQuestionChoicesFrom(
      issues,
      researchQuestions,
      question,
      index,
      questionPath,
    );
  });

  validateCompositeReferences(issues, payload);
  validateDisallowedLogic(issues, payload, "");
  issues.push(...assetComparisonCoherenceIssues(payload));
  issues.push(...exposureCoherenceIssues(payload));
  issues.push(...testedAssetUnusedIssues(payload));
  issues.push(...recalledReadIssues(payload));
  const recipe = studySpecOf(payload as { studySpec?: StudySpec })?.recorded.recipe;
  issues.push(
    ...heldBackReadIssues(
      researchQuestions,
      (recipe ? recipeDeclarations(recipe) : null)?.brandUndisclosedModuleIds ?? [],
    ),
  );
  issues.push(...answerabilityIssues(payload));
  issues.push(...reportingCoherenceIssues(payload));

  const hasError = issues.some((issue) => issue.severity === "error");
  return {
    status: hasError ? "invalid" : "valid",
    issues,
  };
}

export function validateQuestionnaireDraft(
  draft: QuestionnaireDraft,
): DraftValidationResult {
  return validateQuestionnaireDraftPayload(draft);
}

export function getRequiredCompositeBoundsIssue(
  unitId: string,
): QuestionnaireValidationIssue | undefined {
  const unit = getUnitRegistryEntry(unitId);
  if (!unit?.requiresExplicitBoundsForComposite) {
    return undefined;
  }

  return {
    path: "response",
    code: "composite_bounds_required",
    message:
      repairLine("composite_bounds_required"),
    severity: "error",
  };
}
